import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';
import {
  shouldScopePedidosToMedicoUser,
  resolveLinkedMedicoIdFromUser,
  pedidoBelongsToLinkedMedico,
  filterPedidosForMedicoUser
} from '../utils/medicoPedidosScope';
import productos from '../data/productos.json';
import { parsePedidoInternalMessages } from '../utils/pedidoInternalMessages';
import ColaMedicaBadge from '../components/ColaMedicaBadge';
import ColaMedicaFloatingAlert from '../components/ColaMedicaFloatingAlert';
import PedidoNuevoBadge from '../components/PedidoNuevoBadge';
import PacienteExternoBadge from '../components/PacienteExternoBadge';
import {
  getColaMedicaCardVisual,
  getColaMedicaWaitMs,
  getGroupColaCardVisual,
  summarizeColaMedica
} from '../utils/pedidoColaMedica';
import {
  drainPendingPedidosDerivadosMedico,
  subscribePedidosMedicoSync
} from '../utils/pedidosRecetaMedicoSync';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import {
  EMISOR_TRATAMIENTO_ADMIN,
  pedidoRequiereSeleccionMedicoEnAprobacion,
  puedeAdminDerivarMedicoEnAprobacion,
  resolveAdministrativoEmisionEnAprobacion,
  coerceWebhookFlag,
  resolveAprobacionFlujoDefaults,
  resolveAprobacionWebhookDefaults,
  resolveLinkedAdministrativoIdFromUser,
  resolveMedicoTratanteEnAprobacion
} from '../utils/tratamientoAprobacion';
import {
  calcularDosificacion,
  createDosificacionStateFromPedido,
  serializeDosificacionForApi
} from '../utils/tratamientoDosificacion';
import TratamientoAprobacionWizard from '../components/TratamientoAprobacionWizard';
import AltaRapidaPedidoModal from '../components/AltaRapidaPedidoModal';
import { formatMedicamentoOptionLabel } from '../utils/altaRapidaPedido';
import { CrmStatusBadgeFrom } from '../components/CrmStatusBadge';
import { resolvePedidoStatusBadge, resolveRecetaStatusBadge } from '../utils/crmStatusBadge';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/pedidos-recetas-medico-premium.css';
import { PULSE_CMD_FOCUS_PEDIDO_KEY } from '../config/pulseCommand';
import { CwListSearch } from '../components/conversaciones/CwUiParts';
import { canCreatePedidoAltaRapida } from '../utils/userPermissions';

export const initialState = {
  pedidos: [],
  recetas: [],
  pedidos_farmacia: [
    { id: 1, receta_id: 2, paciente: 'María Elena García', medicamento: 'Omeprazol 20mg', direccion: 'San Martín 450, Luján de Cuyo', estado: 'pendiente_call_center', nro_pedido: null, fecha: '2026-03-20' }
  ]
};

export const INITIAL_PENDING_PEDIDOS = initialState.pedidos.filter(p => p.estado === 'pendiente_aprobacion_medica').length;
export const INITIAL_PENDING_RECETAS = initialState.recetas.filter(r => r.estado === 'pendiente').length;

const HISTORIAL = {};
const NUEVO_PEDIDO_TTL_MS = 30 * 1000;

function estadoPillElem(pedidoOrEstado, { compact = false, nowMs } = {}) {
  const pedido =
    pedidoOrEstado && typeof pedidoOrEstado === 'object'
      ? pedidoOrEstado
      : { estado: pedidoOrEstado };
  return (
    <CrmStatusBadgeFrom
      badge={resolvePedidoStatusBadge(pedido, nowMs)}
      compact={compact}
    />
  );
}

function recetaPillElem(recetaOrEstado) {
  return <CrmStatusBadgeFrom badge={resolveRecetaStatusBadge(recetaOrEstado)} />;
}

function parseLocalDate(dateString) {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function normalizeToIsoDate(dateString) {
  if (!dateString) return '';
  try {
    const s = String(dateString).trim();
    if (!s) return '';
    // already ISO-like with time
    if (s.includes('T')) return s.split('T')[0];
    // date with time sep
    if (s.includes(' ')) {
      const maybe = s.split(' ')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(maybe)) return maybe;
    }
    const m = s.match(/(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const m2 = s.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (m2) return `${m2[3]}-${m2[2]}-${m2[1]}`;
    return '';
  } catch (e) {
    return '';
  }
}

function toTitleCaseText(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/(^|[\s(\/+\-])([a-záéíóúñü])/g, (_, sep, ch) => `${sep}${ch.toUpperCase()}`);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text && !['null', 'undefined'].includes(text.toLowerCase())) return text;
  }
  return '';
}

function capitalizeFirstLetter(value) {
  const s = String(value || '').trim();
  if (!s || ['null', 'undefined'].includes(s.toLowerCase())) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function normalizeMedicationFields(source) {
  const cronicaRaw = String(source?.cronica ?? '').trim().toLowerCase();
  const esCronico = source?.es_cronico === true
    || source?.es_cronico === 1
    || ['1', 's', 'si', 'sí', 'true', 'yes'].includes(String(source?.es_cronico ?? '').trim().toLowerCase())
    || ['1', 's', 'si', 'sí', 'true', 'yes', 'cronico', 'crónico'].includes(cronicaRaw);

  return {
    medicamento_nombre: firstNonEmpty(
      source?.producto,
      source?.nombre_comercial,
      source?.medicamento_nombre,
      source?.medicamento,
      source?.nombre_generico,
      source?.medicamento_generico
    ),
    nombre_generico: firstNonEmpty(source?.nombre_generico, source?.medicamento_generico, source?.droganombre),
    nombre_comercial: firstNonEmpty(source?.producto, source?.nombre_comercial),
    laboratorio: firstNonEmpty(source?.laboratorio, source?.medicamento_laboratorio, source?.desclaboratorio, source?.nombrelab),
    presentacion: firstNonEmpty(source?.presentacion, source?.medicamento_presentacion),
    concentracion: firstNonEmpty(source?.concentracion, source?.medicamento_concentracion),
    accion_nombre: firstNonEmpty(source?.accion_nombre, source?.accionnombre),
    categoria_nombre: firstNonEmpty(source?.categoria_nombre, source?.categorianombre, source?.categoria, source?.sub_categoria, source?.subrubronombre),
    condicion_venta: firstNonEmpty(source?.condicion_venta, source?.tipoventadesc),
    refrigeracion: firstNonEmpty(source?.refrigeracion),
    es_cronico: esCronico,
    cronica: firstNonEmpty(source?.cronica, esCronico ? 'si' : '')
  };
}

function buildMedicationDisplay(pedido) {
  const details = normalizeMedicationFields(pedido);
  const isCronicoCategory = ['cronico', 'crónico'].includes(String(details.categoria_nombre || '').trim().toLowerCase());
  const comercial = firstNonEmpty(
    pedido?.nombre_comercial,
    pedido?.producto,
    details.nombre_comercial,
    details.medicamento_nombre,
    pedido?.medicamento
  );
  const generico = capitalizeFirstLetter(details.nombre_generico);
  const presentacion = details.presentacion;
  const concentracionRaw = details.concentracion;
  const laboratorioRaw = details.laboratorio;
  const laboratorio = laboratorioRaw && laboratorioRaw !== '0' ? laboratorioRaw : '';
  const fallback = firstNonEmpty(details.medicamento_nombre, pedido?.medicamento);
  const title = comercial || fallback || generico || '—';
  const titleKey = title.toLowerCase();
  const concentrationKey = concentracionRaw.toLowerCase();
  const titleContainsConcentration = concentrationKey && titleKey.includes(concentrationKey);
  const presentationContainsConcentration = concentrationKey && presentacion.toLowerCase().includes(concentrationKey);
  const concentracion = /[a-z]/i.test(concentracionRaw) && !titleContainsConcentration && !presentationContainsConcentration
    ? concentracionRaw
    : '';
  const genericLine = generico && generico.toLowerCase() !== titleKey
    ? `Droga: ${generico}`
    : '';
  const accionNombre = capitalizeFirstLetter(details.accion_nombre);
  const categoriaNombre = isCronicoCategory ? '' : capitalizeFirstLetter(details.categoria_nombre);
  const requerimientos = [
    details.es_cronico ? 'Crónico' : '',
    String(details.refrigeracion || '').trim().toUpperCase() === 'S' ? 'Refrigerado' : ''
  ].filter(Boolean);
  const laboratoryLabel = laboratorio ? `Lab. ${laboratorio}` : '';
  const meta = [
    concentracion,
    presentacion,
    accionNombre,
    categoriaNombre,
    ...requerimientos
  ].filter(Boolean);

  const detailLines = [
    details.condicion_venta ? `Condición de venta: ${details.condicion_venta}. Requiere indicación médica.` : ''
  ].filter(Boolean);

  return { title, genericLine, laboratoryLabel, meta, detailLines };
}

function normalizeMedicosFromApi(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.medicos) ? payload.medicos : []);

  const cleaned = raw.map((item, idx) => {
    if (typeof item === 'string') {
      const nombre = item.trim();
      return nombre ? { id: `prestador-${idx}-${nombre}`, nombre, especialidad: null, matricula: null } : null;
    }
    if (!item || typeof item !== 'object') return null;
    const nombre = String(item.nombre || item.prestador_geclisa || '').trim();
    if (!nombre) return null;
    const id = item.id != null ? String(item.id) : `prestador-${idx}-${nombre}`;
    return {
      id,
      nombre,
      especialidad: item.especialidad || null,
      matricula: item.matricula || null,
      genero: item.genero || item.sexo || item.sex || null
    };
  }).filter(Boolean);

  const uniqueByName = new Map();
  cleaned.forEach((m) => {
    const key = String(m.nombre || '').toLowerCase();
    if (!key) return;
    if (!uniqueByName.has(key)) uniqueByName.set(key, m);
  });
  return Array.from(uniqueByName.values());
}

function formatDoctorDisplayName(nombre, genero) {
  const raw = String(nombre || '').trim();
  if (!raw) return '—';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  const g = String(genero || '').trim().toLowerCase();
  const isFemaleByGenero = g.startsWith('f') || g.includes('muj');
  const isMaleByGenero = g.startsWith('m') || g.includes('hom');
  const isFemaleByName = /\b(maria|ana|paula|rocio|barbara|agustina|laura|sofia|gabriela|florencia|carla|soledad)\b/i.test(raw);
  const prefix = isFemaleByGenero || (!isMaleByGenero && isFemaleByName) ? 'Dra.' : 'Dr.';
  return `${prefix} ${raw}`;
}

function resolvePedidoInternalDoctorLabel(message, pedido) {
  if (!message) return '';
  const rawAuthor = String(message.author || '').trim();
  if (message.tone === 'medico' && /^(medico|médico)$/i.test(rawAuthor)) {
    const doctorName = String(pedido?.medico || '').trim();
    return doctorName ? formatDoctorDisplayName(doctorName) : 'Médico';
  }
  return rawAuthor;
}

function resolveRecetasEmitidas(source) {
  const candidates = [
    source?.recetas_generadas,
    source?.recetas_emitidas,
    source?.cantidad_emitidas,
    source?.emitidas,
    source?.recetas_emitidas_count
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return 0;
}

function formatDniDisplay(dni) {
  const digits = String(dni || '').replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return '—';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Clave estable para agrupar pedidos del mismo paciente. */
function normalizePedidoDniKey(pedido) {
  const dni = String(pedido?.paciente_dni || pedido?.dni || pedido?.dni_normalizado || '')
    .replace(/\D/g, '')
    .replace(/^0+/, '');
  return dni || `id-${pedido?.id}`;
}

function cardPatientInitials(name) {
  return String(name || '')
    .replace(/,/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'P';
}

function formatMedicoTitulo(rawName) {
  const name = String(rawName || '').trim();
  if (!name) return '';
  const lower = name.toLowerCase();
  if (lower.startsWith('dra. ') || lower.startsWith('dra ') || lower.startsWith('doctora ')) return name;
  if (lower.startsWith('dr. ') || lower.startsWith('dr ') || lower.startsWith('doctor ')) return name;
  const parts = name.split(/\s+/).filter(Boolean);
  const givenName = parts.length ? parts[parts.length - 1] : name;
  const normalized = givenName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return `${normalized.endsWith('a') ? 'Dra.' : 'Dr.'} ${name}`;
}

function isFamilyRequest(pedido) {
  const solicitante = String(pedido?.solicitante_dni || '').replace(/\D/g, '').replace(/^0+/, '');
  const paciente = String(pedido?.paciente_dni || '').replace(/\D/g, '').replace(/^0+/, '');
  return Boolean(solicitante && paciente && solicitante !== paciente);
}

registerLocale('es', es);

function formatSolicitud(dateString) {
  if (!dateString) return '';
  try {
    const s = String(dateString).trim();
    const pad = (n) => String(n).padStart(2, '0');
    const onlyDate = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (onlyDate) {
      return `${Number(onlyDate[3])}/${Number(onlyDate[2])}/${Number(onlyDate[1])}`;
    }
    let d;
    // If the string includes a timezone (Z or +/-HH:MM), parse as ISO/local browser timezone
    if (/[Zz]$/.test(s) || /[+\-]\d{2}:?\d{2}$/.test(s)) {
      d = new Date(s);
    } else {
      // fallback: parse naive 'YYYY-MM-DD HH:MM:SS[.micro]'
      const m = s.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/);
      if (!m) return '';
      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);
      const hour = Number(m[4]);
      const minute = Number(m[5]);
      const second = Number(m[6]);
      d = new Date(year, month - 1, day, hour, minute, second);
    }
    if (Number.isNaN(d.getTime())) return '';
    const dayNum = d.getDate();
    const monthNum = d.getMonth() + 1;
    const yearNum = d.getFullYear();
    const h = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${dayNum}/${monthNum}/${yearNum}, ${h}:${min} hs`;
  } catch (e) {
    return '';
  }
}

const MEDICAMENTO_PLACEHOLDER = 'Cargando medicamento...';

function isMedicamentoPlaceholder(name) {
  const t = String(name || '').trim();
  return !t || t === MEDICAMENTO_PLACEHOLDER;
}

function mergePedidoMedicamentoFields(prev, incoming) {
  if (!incoming) return prev;
  if (!prev) return incoming;
  const keepNombre = !isMedicamentoPlaceholder(prev.medicamento) && isMedicamentoPlaceholder(incoming.medicamento);
  return {
    ...incoming,
    medicamento: keepNombre ? prev.medicamento : (incoming.medicamento || prev.medicamento),
    medicamento_generico: incoming.medicamento_generico || prev.medicamento_generico || null,
    medicamento_concentracion: incoming.medicamento_concentracion || prev.medicamento_concentracion || '',
    medicamento_presentacion: incoming.medicamento_presentacion || prev.medicamento_presentacion || '',
    medicamento_laboratorio: incoming.medicamento_laboratorio || prev.medicamento_laboratorio || '',
    nombre_comercial: incoming.nombre_comercial || prev.nombre_comercial || '',
    medico: incoming.medico || prev.medico || '',
  };
}

function pedidoNeedsMedicamentoHydration(p, productoMap) {
  const mid = Number(p?.medicamento_id || 0);
  if (!mid) return false;
  const missingName = isMedicamentoPlaceholder(p.medicamento) && !productoMap.get(mid);
  const missingGeneric = !(p.medicamento_generico);
  const missingConcentration = !(p.medicamento_concentracion);
  const missingPresentation = !(p.medicamento_presentacion);
  const missingBrand = !(p.nombre_comercial);
  return missingName || missingGeneric || missingConcentration || missingPresentation || missingBrand;
}

function mapRawPedidoRow(r, productoMap) {
  const mid = Number(r.medicamento_id);
  const medName = r.medicamento_nombre || r.medicamento || productoMap.get(mid) || (mid ? MEDICAMENTO_PLACEHOLDER : '');
  return {
    id: r.id,
    solicitante_dni: r.solicitante_dni,
    paciente_dni: r.paciente_dni,
    paciente: r.paciente_nombre || r.paciente || r.paciente_dni || r.solicitante_dni || 'Paciente',
    dni: r.paciente_dni || r.solicitante_dni || '',
    medicamento_id: r.medicamento_id,
    medicamento: medName,
    obra_social: r.obra_social_descripcio || r.obra_social || r.os_sigla_geclisa || '',
    medicamento_generico: r.nombre_generico || r.medicamento_generico || null,
    medicamento_concentracion: r.concentracion || r.medicamento_concentracion || '',
    medicamento_presentacion: r.presentacion || r.medicamento_presentacion || '',
    medicamento_laboratorio: r.laboratorio || r.medicamento_laboratorio || '',
    nombre_comercial: r.nombre_comercial || '',
    solicitado: r.fecha_solicitud ? formatSolicitud(r.fecha_solicitud) : '',
    fecha_solicitud: r.fecha_solicitud,
    fecha_derivacion_medica: r.fecha_derivacion_medica || null,
    estado: String(r.estado || '').trim().toLowerCase() || r.estado,
    acepta_recordatorios: !!r.acepta_recordatorios,
    motivo_rechazo: r.motivo_rechazo || null,
    notas: r.notas || '',
    message_history: r.message_history || '',
    medico_id: r.medico_id,
    medico: r.medico || r.prestador_geclisa || '',
    prestador_geclisa: r.prestador_geclisa || '',
    cantidad_recetas: r.cantidad_recetas || 0,
    recetas_generadas: resolveRecetasEmitidas(r),
    posologia: r.posologia || '',
    es_pami: r.es_pami === 1 || r.es_pami === true,
    es_externo: r.es_externo === 1 || r.es_externo === true,
    cliente_externo_id: r.cliente_externo_id ?? null
  };
}

export default function PedidosRecetasMedico() {
  const { user, permissionsLoading, refreshPermissions } = useAuth();
  const isAdmin = Boolean(user?.isAdmin);
  const canAltaRapida = useMemo(
    () => canCreatePedidoAltaRapida(user, { permissionsLoading }),
    [user, permissionsLoading]
  );

  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);
  const pedidosScopedToMedico = useMemo(() => shouldScopePedidosToMedicoUser(user), [user]);
  const [linkedMedicoId, setLinkedMedicoId] = useState(() => resolveLinkedMedicoIdFromUser(user));

  useEffect(() => {
    if (!pedidosScopedToMedico) {
      setLinkedMedicoId(null);
      return undefined;
    }
    const fromUser = resolveLinkedMedicoIdFromUser(user);
    if (fromUser) {
      setLinkedMedicoId(fromUser);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/profile`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const profile = await res.json();
        if (!cancelled) setLinkedMedicoId(resolveLinkedMedicoIdFromUser(profile));
      } catch (_) {
        if (!cancelled) setLinkedMedicoId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, pedidosScopedToMedico]);

  const applyMedicoScopeToPedidos = useCallback(
    (pedidos) => filterPedidosForMedicoUser(pedidos, user, linkedMedicoId),
    [user, linkedMedicoId]
  );

  // Nuevo estado para saber cuál pedido está en aprobación
  const [aprobandoPedidoId, setAprobandoPedidoId] = useState(null);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1366,
    height: typeof window !== 'undefined' ? window.innerHeight : 768
  }));
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const isMobile = viewport.width <= 768;
  const isTablet = viewport.width > 768 && viewport.width <= 1100;
  const isMidDesktop = viewport.width > 768 && viewport.width <= 1450;
  const isSmallLandscape = viewport.width <= 1024 && viewport.width > viewport.height;
  const isNarrow = viewport.width < 500;

  const [data, setData] = useState(initialState);
  const medicosMapRef = useRef(new Map());
  const [medicosList, setMedicosList] = useState([]);
  const [medicosDerivacion, setMedicosDerivacion] = useState([]);
  const [administrativosEmision, setAdministrativosEmision] = useState([]);
  const [administrativosLoading, setAdministrativosLoading] = useState(true);
  const [pedidosLoading, setPedidosLoading] = useState(true);
  const [medicosLoading, setMedicosLoading] = useState(true);
  const navigate = useNavigate();
  const mergedMedicos = useMemo(() => (Array.isArray(medicosList) ? medicosList : []), [medicosList]);
  const linkedMedicoNombre = useMemo(() => {
    if (!linkedMedicoId) return '';
    const match = mergedMedicos.find((m) => Number(m.id) === Number(linkedMedicoId));
    return match?.nombre || '';
  }, [linkedMedicoId, mergedMedicos]);
  const linkedAdministrativoId = useMemo(
    () => resolveLinkedAdministrativoIdFromUser(user),
    [user]
  );
  const linkedAdministrativoNombre = useMemo(() => {
    if (!linkedAdministrativoId) return String(user?.administrativo_nombre || '').trim();
    const match = administrativosEmision.find((a) => Number(a.id) === Number(linkedAdministrativoId));
    return match?.nombre || String(user?.administrativo_nombre || '').trim();
  }, [linkedAdministrativoId, administrativosEmision, user]);
  const buildMedicosFromPedidos = (rows) => {
    const byName = new Map();
    (Array.isArray(rows) ? rows : []).forEach((r) => {
      const nombre = String(r?.medico || r?.prestador_geclisa || '').trim();
      if (!nombre) return;
      const key = nombre.toLowerCase();
      if (!byName.has(key)) byName.set(key, { id: `pedido-${key}`, nombre, especialidad: null, matricula: null });
    });
    return Array.from(byName.values());
  };
  // Crear pedido (llama al backend y actualiza state)
  async function crearPedido(body) {
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const nuevoRaw = await res.json();
      const productoMapNew = new Map(productos.map(p => [Number(p.id), p.nombre]));
      const midNew = Number(nuevoRaw.medicamento_id);
      const medNameNew = nuevoRaw.medicamento_nombre || nuevoRaw.medicamento || productoMapNew.get(midNew) || (midNew ? 'Cargando medicamento...' : '');
      const nuevo = {
        id: nuevoRaw.id,
        solicitante_dni: nuevoRaw.solicitante_dni,
        paciente_dni: nuevoRaw.paciente_dni,
        paciente: nuevoRaw.paciente_nombre || nuevoRaw.paciente || nuevoRaw.paciente_dni || nuevoRaw.solicitante_dni || 'Paciente',
        dni: nuevoRaw.paciente_dni || nuevoRaw.solicitante_dni || '',
        medicamento_id: nuevoRaw.medicamento_id,
        medicamento: medNameNew,
        obra_social: nuevoRaw.obra_social_descripcio || nuevoRaw.obra_social || nuevoRaw.os_sigla_geclisa || '',
        nombre_comercial: nuevoRaw.nombre_comercial || '',
        solicitado: nuevoRaw.fecha_solicitud ? formatSolicitud(nuevoRaw.fecha_solicitud) : '',
        fecha_solicitud: nuevoRaw.fecha_solicitud,
        estado: nuevoRaw.estado,
        acepta_recordatorios: !!nuevoRaw.acepta_recordatorios,
        motivo_rechazo: nuevoRaw.motivo_rechazo || null,
        notas: nuevoRaw.notas || '',
        medico_id: nuevoRaw.medico_id,
        cantidad_recetas: nuevoRaw.cantidad_recetas || 0,
        recetas_generadas: resolveRecetasEmitidas(nuevoRaw),
        posologia: nuevoRaw.posologia || ''
      };
      setData(d => ({ ...d, pedidos: [nuevo, ...d.pedidos] }));
      toast('Pedido creado', 'success');
      return nuevo;
    } catch (error) {
      console.error('Error creando pedido:', error);
      toast('Error creando pedido', 'error');
      throw error;
    }
  }
  const [panel, setPanel] = useState('medico');

  // modals
  const [modalAprobarOpen, setModalAprobarOpen] = useState(false);
  const [aprobarWizardEpoch, setAprobarWizardEpoch] = useState(0);
  const [modalRechazarOpen, setModalRechazarOpen] = useState(false);
  const [rechazoSubmitting, setRechazoSubmitting] = useState(false);
  const [modalEmitirOpen, setModalEmitirOpen] = useState(false);
  const [modalFarmaciaOpen, setModalFarmaciaOpen] = useState(false);
  const [modalEstadoOpen, setModalEstadoOpen] = useState(false);
  const [modalHistorialGlobalOpen, setModalHistorialGlobalOpen] = useState(false);
  const [modalVerRecetaOpen, setModalVerRecetaOpen] = useState(false);
  const [verRecetaPedidoId, setVerRecetaPedidoId] = useState(null);
  const [modalAltaRapidaOpen, setModalAltaRapidaOpen] = useState(false);
  const [editarMedicamentoPedidoId, setEditarMedicamentoPedidoId] = useState(null);
  const [editarMedicamentoSearch, setEditarMedicamentoSearch] = useState('');
  const [editarMedicamentos, setEditarMedicamentos] = useState([]);
  const [editarMedicamentosLoading, setEditarMedicamentosLoading] = useState(false);
  const [editarMedicamentoPickerOpen, setEditarMedicamentoPickerOpen] = useState(false);
  const [editarMedicamentoChosen, setEditarMedicamentoChosen] = useState(null);
  const [editarMedicamentoSaving, setEditarMedicamentoSaving] = useState(false);
  const editarMedicamentoPickerRef = useRef(null);
  const [historialGlobalDni, setHistorialGlobalDni] = useState('');
  const [historialGlobalSearch, setHistorialGlobalSearch] = useState('');
  const [historialGlobalPacienteSearch, setHistorialGlobalPacienteSearch] = useState('');
  const [historialGlobalDesde, setHistorialGlobalDesde] = useState('');
  const [historialGlobalHasta, setHistorialGlobalHasta] = useState('');
  const [historialGlobalEstado, setHistorialGlobalEstado] = useState('todos');
  const [historialGlobalOrder, setHistorialGlobalOrder] = useState('fecha_desc');
  const [historialGlobalPage, setHistorialGlobalPage] = useState(1);
  const [historialGlobalItemsPerPage, setHistorialGlobalItemsPerPage] = useState(6);
  const [historialEmitidasByPedido, setHistorialEmitidasByPedido] = useState({});
  const [historialRecetasByPedido, setHistorialRecetasByPedido] = useState({});

  const [selectedPedidoId, setSelectedPedidoId] = useState(null);
  const pendingPedidoFocusRef = useRef(null);

  useEffect(() => {
    try {
      const rawId = sessionStorage.getItem(PULSE_CMD_FOCUS_PEDIDO_KEY);
      if (rawId) {
        sessionStorage.removeItem(PULSE_CMD_FOCUS_PEDIDO_KEY);
        const pedidoId = Number(rawId);
        if (Number.isFinite(pedidoId) && pedidoId > 0) pendingPedidoFocusRef.current = pedidoId;
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const pedidoId = pendingPedidoFocusRef.current;
    if (!pedidoId || pedidosLoading) return;
    if (!data.pedidos.some((p) => Number(p.id) === pedidoId)) return;
    pendingPedidoFocusRef.current = null;
    setSelectedPedidoId(pedidoId);
    setHistoryOpen(true);
    setOpenItems((prev) => (prev.includes(pedidoId) ? prev : [...prev, pedidoId]));
  }, [data.pedidos, pedidosLoading]);
  const [selectedRecetaId, setSelectedRecetaId] = useState(null);
  const [selectedFarmaciaId, setSelectedFarmaciaId] = useState(null);
  const [search, setSearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyItemsPerPage, setHistoryItemsPerPage] = useState(5);
  const [historyOpen, setHistoryOpen] = useState(false);

  // form fields for aprobar
  const [aprobarMeses, setAprobarMeses] = useState(1);
  const [aprobarFecha, setAprobarFecha] = useState(() => new Date().toISOString().split('T')[0]);
  const [aprobarPosologia, setAprobarPosologia] = useState('');
  const [aprobarCantidad, setAprobarCantidad] = useState(1);
  const [aprobarDosificacion, setAprobarDosificacion] = useState(() => createDosificacionStateFromPedido());
  const [aprobarMedicoSeleccion, setAprobarMedicoSeleccion] = useState('');
  const [aprobarMedicoBuscar, setAprobarMedicoBuscar] = useState('');
  const [aprobarEmisorTratamiento, setAprobarEmisorTratamiento] = useState('medico');
  const [aprobarAdministrativoSeleccion, setAprobarAdministrativoSeleccion] = useState('');
  const [aprobarWebhookAlta, setAprobarWebhookAlta] = useState(true);
  const [aprobarWebhookEmitir, setAprobarWebhookEmitir] = useState(false);

  // emitir
  const [emitirNro, setEmitirNro] = useState('');

  // farmacia
  const [farmaciaNro, setFarmaciaNro] = useState('');
  const [farmaciaNotas, setFarmaciaNotas] = useState('');

  // toast
  const [toasts, setToasts] = useState([]);
  const [rechazoMotivo, setRechazoMotivo] = useState('');
  const [internalMessageDrafts, setInternalMessageDrafts] = useState({});
  const [internalMessageSaving, setInternalMessageSaving] = useState({});
  const [internalChatPedidoId, setInternalChatPedidoId] = useState(null);

  // Accordion state for cards
  const [openItems, setOpenItems] = useState([]);
  const [collapsedPatientGroups, setCollapsedPatientGroups] = useState(() => new Set());
  const contentRefs = useRef({});
  const nuevosPedidosRef = useRef(new Map());
  const [nuevosTick, setNuevosTick] = useState(0);

  const markPedidoNuevo = useCallback((id) => {
    if (id == null || id === '') return;
    nuevosPedidosRef.current.set(String(id), Date.now() + NUEVO_PEDIDO_TTL_MS);
    setNuevosTick((t) => t + 1);
  }, []);

  const clearPedidoNuevo = useCallback((id) => {
    if (id == null || id === '') return;
    if (nuevosPedidosRef.current.delete(String(id))) setNuevosTick((t) => t + 1);
  }, []);

  const isPedidoNuevo = useCallback((id) => {
    void nuevosTick;
    const key = String(id);
    const expiresAt = nuevosPedidosRef.current.get(key);
    if (!expiresAt) return false;
    if (Date.now() >= expiresAt) {
      nuevosPedidosRef.current.delete(key);
      return false;
    }
    return true;
  }, [nuevosTick]);

  const togglePedidoCard = useCallback((id) => {
    const key = Number(id);
    setOpenItems((prev) => {
      const opening = !prev.some((x) => Number(x) === key);
      if (opening) clearPedidoNuevo(key);
      return opening ? [key] : [];
    });
  }, [clearPedidoNuevo]);

  const isPatientGroupExpanded = useCallback(
    (dniKey) => !collapsedPatientGroups.has(String(dniKey)),
    [collapsedPatientGroups]
  );

  const togglePatientGroup = useCallback((dniKey) => {
    const key = String(dniKey);
    setCollapsedPatientGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const internalChatPedido = useMemo(
    () => data.pedidos.find((pedido) => Number(pedido.id) === Number(internalChatPedidoId)) || null,
    [data.pedidos, internalChatPedidoId]
  );


  useEffect(() => {
    // cleanup old toasts
    const id = setInterval(() => {
      setToasts((t) => t.slice(0, 5));
    }, 3000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (internalChatPedidoId == null) return;
    const exists = data.pedidos.some((pedido) => Number(pedido.id) === Number(internalChatPedidoId));
    if (!exists) setInternalChatPedidoId(null);
  }, [data.pedidos, internalChatPedidoId]);

  function toast(msg, type = 'info') {
    const id = Date.now() + Math.random();
    // support structured payload: { paciente, sus } or simple string
    if (msg && typeof msg === 'object' && (msg.paciente || msg.text)) {
      setToasts((t) => [...t, { id, type, ...msg }]);
    } else {
      setToasts((t) => [...t, { id, type, msg: String(msg || '') }]);
    }
    setTimeout(() => setToasts((t) => t.filter(x => x.id !== id)), 4000);
  }

  const hydrateMedicamentosPedidos = useCallback(async (pedidosList) => {
    const productoMap = new Map(productos.map((p) => [Number(p.id), p.nombre]));
    const ids = Array.from(new Set(
      (Array.isArray(pedidosList) ? pedidosList : [])
        .filter((p) => pedidoNeedsMedicamentoHydration(p, productoMap))
        .map((p) => Number(p.medicamento_id))
        .filter((id) => Number.isFinite(id) && id > 0)
    ));
    if (!ids.length) return;
    await Promise.all(ids.map(async (mid) => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/medicamento/${mid}`, { credentials: 'include' });
        if (!r.ok) return;
        const jm = await r.json();
        const nombre = jm.nombre_comercial || jm.nombre_generico || jm.nombre || '';
        setData((d) => ({
          ...d,
          pedidos: (d.pedidos || []).map((p) => {
            if (Number(p.medicamento_id) !== mid) return p;
            return {
              ...p,
              medicamento: isMedicamentoPlaceholder(p.medicamento) ? (nombre || p.medicamento) : p.medicamento,
              medicamento_generico: jm.nombre_generico || p.medicamento_generico || '',
              medicamento_concentracion: jm.concentracion || p.medicamento_concentracion || '',
              medicamento_presentacion: jm.presentacion || p.medicamento_presentacion || '',
              medicamento_laboratorio: jm.laboratorio || p.medicamento_laboratorio || '',
              nombre_comercial: jm.nombre_comercial || p.nombre_comercial || '',
            };
          }),
        }));
      } catch (_) { /* ignore */ }
    }));
  }, []);

  useEffect(() => {
    if (!user?.id) return undefined;
    const ac = new AbortController();
    const { signal } = ac;
    const productoMap = new Map(productos.map((p) => [Number(p.id), p.nombre]));

    const mapRows = (raw) => (Array.isArray(raw) ? raw : []).map((r) => mapRawPedidoRow(r, productoMap));

    const mergePedidosState = (incoming) => {
      if (!incoming?.length) return;
      setData((d) => {
        const byId = new Map((d.pedidos || []).map((p) => [String(p.id), p]));
        incoming.forEach((p) => {
          const prev = byId.get(String(p.id));
          byId.set(String(p.id), prev ? mergePedidoMedicamentoFields(prev, p) : p);
        });
        return { ...d, pedidos: Array.from(byId.values()) };
      });
    };

    const fetchJson = async (url) => {
      try {
        if (signal.aborted) return null;
        const res = await fetch(url, { credentials: 'include', signal });
        if (!res.ok) return null;
        return await res.json().catch(() => null);
      } catch (err) {
        if (err?.name === 'AbortError' || signal.aborted) return null;
        throw err;
      }
    };

    const safeFetchJson = (url) => fetchJson(url).catch((err) => {
      if (err?.name === 'AbortError' || signal.aborted) return null;
      return Promise.reject(err);
    });

    void (async () => {
      setPedidosLoading(true);
      setMedicosLoading(true);
      setAdministrativosLoading(true);
      let pedidosPainted = false;

      const paintPedidosReady = () => {
        if (signal.aborted || pedidosPainted) return;
        pedidosPainted = true;
        setPedidosLoading(false);
      };

      try {
        const pendientesP = safeFetchJson(`${API_BASE_URL}/api/asociacion-recetas/pedidos-pendientes`);
        const allPedidosP = safeFetchJson(`${API_BASE_URL}/api/asociacion-recetas/pedidos`);
        const medicosP = safeFetchJson(`${API_BASE_URL}/api/asociacion-recetas/medicos?all=1`);
        const medicosDerivacionP = safeFetchJson(`${API_BASE_URL}/api/asociacion-recetas/medicos`);
        const administrativosP = safeFetchJson(`${API_BASE_URL}/api/asociacion-recetas/administrativos`);

        const pendientesJson = await pendientesP;
        if (!signal.aborted && pendientesJson) {
          const pendientes = mapRows(Array.isArray(pendientesJson?.pedidos) ? pendientesJson.pedidos : []);
          mergePedidosState(pendientes);
          paintPedidosReady();
          void hydrateMedicamentosPedidos(pendientes);
        }

        const [allJson, medicosJson, derivacionJson, adminJson] = await Promise.all([
          allPedidosP,
          medicosP,
          medicosDerivacionP,
          administrativosP
        ]);

        if (signal.aborted) return;

        const rawAll = allJson
          ? (Array.isArray(allJson?.pedidos) ? allJson.pedidos : (Array.isArray(allJson) ? allJson : []))
          : [];
        if (rawAll.length) {
          const allPedidos = mapRows(rawAll);
          mergePedidosState(allPedidos);
          void hydrateMedicamentosPedidos(allPedidos);
        }
        paintPedidosReady();

        const list = normalizeMedicosFromApi(medicosJson || {});
        const derivacionList = normalizeMedicosFromApi(derivacionJson || {});
        const administrativosList = Array.isArray(adminJson?.administrativos) ? adminJson.administrativos : [];
        const finalList = list.length ? list : buildMedicosFromPedidos(rawAll);
        const map = new Map(finalList.map((m) => [String(m.id), m.nombre]));
        medicosMapRef.current = map;
        setMedicosList(finalList);
        setMedicosDerivacion(derivacionList.length ? derivacionList : finalList);
        setMedicosLoading(false);
        setAdministrativosEmision(administrativosList);
        setAdministrativosLoading(false);
        if (map.size) {
          setData((d) => ({
            ...d,
            pedidos: (d.pedidos || []).map((p) => ({
              ...p,
              medico: p.medico || map.get(String(p.medico_id)) || p.medico
            }))
          }));
        }
      } catch (err) {
        if (err?.name !== 'AbortError' && !signal.aborted) {
          console.error('Error cargando pedidos:', err);
        }
        paintPedidosReady();
        if (!signal.aborted) {
          setMedicosLoading(false);
          setAdministrativosLoading(false);
        }
      }
    })().catch((err) => {
      if (err?.name !== 'AbortError') {
        console.error('Error cargando pedidos:', err);
      }
    });

    return () => ac.abort();
  }, [user?.id, hydrateMedicamentosPedidos]);

  const refetchPedidosPendientesMedico = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-pendientes`, { credentials: 'include' });
      if (!res.ok) return;
      const json = await res.json();
      const raw = Array.isArray(json) ? json : (Array.isArray(json?.pedidos) ? json.pedidos : []);
      const productoMap = new Map(productos.map((p) => [Number(p.id), p.nombre]));
      const incoming = raw.map((r) => mapRawPedidoRow({ ...r, estado: 'pendiente_aprobacion_medica' }, productoMap));
      setData((d) => {
        const byId = new Map((d.pedidos || []).map((p) => [String(p.id), p]));
        incoming.forEach((p) => {
          const prev = byId.get(String(p.id));
          byId.set(String(p.id), mergePedidoMedicamentoFields(prev, p));
        });
        const merged = Array.from(byId.values());
        const pendientes = merged.filter((p) => String(p.estado || '').trim().toLowerCase() === 'pendiente_aprobacion_medica');
        const otros = merged.filter((p) => String(p.estado || '').trim().toLowerCase() !== 'pendiente_aprobacion_medica');
        return { ...d, pedidos: [...pendientes, ...otros] };
      });
      await hydrateMedicamentosPedidos(incoming);
    } catch (err) {
      console.error('Error refrescando pedidos pendientes médico:', err);
    }
  }, [hydrateMedicamentosPedidos]);

  const ingestPedidoDerivadoAlMedico = useCallback(async (pedidoRaw, medicoRow) => {
    if (!pedidoRaw || pedidoRaw.id == null) return;
    const merged = { ...pedidoRaw, estado: 'pendiente_aprobacion_medica' };
    if (!pedidoBelongsToLinkedMedico(merged, linkedMedicoId, pedidosScopedToMedico)) return;
    if (medicoRow?.nombre) {
      merged.medico = medicoRow.nombre;
      merged.medico_id = medicoRow.id ?? merged.medico_id;
    }
    const productoMap = new Map(productos.map((p) => [Number(p.id), p.nombre]));
    const nuevo = mapRawPedidoRow(merged, productoMap);
    setData((d) => {
      const list = Array.isArray(d.pedidos) ? [...d.pedidos] : [];
      const idx = list.findIndex((p) => String(p.id) === String(nuevo.id));
      if (idx >= 0) {
        list[idx] = mergePedidoMedicamentoFields(list[idx], { ...list[idx], ...nuevo, estado: 'pendiente_aprobacion_medica' });
      } else {
        list.unshift(nuevo);
      }
      return { ...d, pedidos: list };
    });
    const nuevoId = Number(nuevo.id);
    const groupKey = normalizePedidoDniKey(nuevo);
    setCollapsedPatientGroups((prev) => {
      if (!prev.has(groupKey)) return prev;
      const next = new Set(prev);
      next.delete(groupKey);
      return next;
    });
    setOpenItems([nuevoId]);
    setSelectedPedidoId(nuevoId);
    setHistoryOpen(false);
    setHistoryPage(1);
    setSearch('');
    await hydrateMedicamentosPedidos([nuevo]);
    markPedidoNuevo(nuevo.id);
    const sus = nuevo.acepta_recordatorios ? ' · Suscripto a recordatorios' : '';
    toast(`Nuevo pedido de ${nuevo.paciente}${sus}`, 'info');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`card-pedido-${nuevo.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
    await refetchPedidosPendientesMedico();
  }, [markPedidoNuevo, refetchPedidosPendientesMedico, hydrateMedicamentosPedidos, linkedMedicoId, pedidosScopedToMedico]);

  useEffect(() => {
    if (!pedidosScopedToMedico) return;
    setData((d) => ({
      ...d,
      pedidos: applyMedicoScopeToPedidos(d.pedidos || [])
    }));
  }, [linkedMedicoId, pedidosScopedToMedico, applyMedicoScopeToPedidos]);

  const aplicarDerivacionesPendientes = useCallback(() => {
    const pending = drainPendingPedidosDerivadosMedico();
    pending.forEach(({ pedido, medico }) => ingestPedidoDerivadoAlMedico(pedido, medico));
  }, [ingestPedidoDerivadoAlMedico]);

  useEffect(() => {
    return subscribePedidosMedicoSync(({ pedido, medico }) => {
      ingestPedidoDerivadoAlMedico(pedido, medico);
    });
  }, [ingestPedidoDerivadoAlMedico]);

  useEffect(() => {
    if (pedidosLoading) return;
    aplicarDerivacionesPendientes();
  }, [pedidosLoading, aplicarDerivacionesPendientes]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || pedidosLoading) return;
      aplicarDerivacionesPendientes();
      refetchPedidosPendientesMedico();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [aplicarDerivacionesPendientes, refetchPedidosPendientesMedico, pedidosLoading]);

  const [colaTick, setColaTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setColaTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      let changed = false;
      nuevosPedidosRef.current.forEach((expiresAt, key) => {
        if (now >= expiresAt) {
          nuevosPedidosRef.current.delete(key);
          changed = true;
        }
      });
      if (changed) setNuevosTick((t) => t + 1);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const scopedPedidos = useMemo(
    () => applyMedicoScopeToPedidos(data.pedidos || []),
    [data.pedidos, applyMedicoScopeToPedidos]
  );

  const pendientesMedico = useMemo(
    () => scopedPedidos.filter((p) => String(p.estado || '').trim().toLowerCase() === 'pendiente_aprobacion_medica'),
    [scopedPedidos]
  );
  const pendientesPorCola = useMemo(
    () => [...pendientesMedico].sort((a, b) => getColaMedicaWaitMs(b, colaTick) - getColaMedicaWaitMs(a, colaTick)),
    [pendientesMedico, colaTick]
  );
  const colaResumen = useMemo(() => summarizeColaMedica(pendientesMedico, colaTick), [pendientesMedico, colaTick]);
  const filteredPendientes = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return pendientesPorCola;
    return pendientesPorCola.filter(p => (
      String(p.paciente || '').toLowerCase().includes(q) ||
      String(p.dni || '').toLowerCase().includes(q) ||
      String(p.medicamento || '').toLowerCase().includes(q)
    ));
  }, [pendientesPorCola, search]);
  const scrollToPrimerPedidoCola = useCallback(() => {
    const first = filteredPendientes[0];
    if (!first) return;
    setOpenItems([Number(first.id)]);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(`card-pedido-${first.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }, [filteredPendientes]);
  const pendientesAgrupadosPorPaciente = useMemo(() => {
    const map = new Map();
    filteredPendientes.forEach((p) => {
      const dniKey = normalizePedidoDniKey(p);
      if (!map.has(dniKey)) map.set(dniKey, { dniKey, pedidos: [] });
      map.get(dniKey).pedidos.push(p);
    });
    return Array.from(map.values())
      .map((g) => {
        const pedidos = g.pedidos;
        const primary = pedidos[0];
        const maxWait = Math.max(...pedidos.map((p) => getColaMedicaWaitMs(p, colaTick)));
        return {
          ...g,
          pedidos,
          primary,
          maxWait,
          paciente: primary?.paciente,
          dni: primary?.dni,
          obra_social: primary?.obra_social
        };
      })
      .sort((a, b) => b.maxWait - a.maxWait);
  }, [filteredPendientes, colaTick]);
  useEffect(() => {
    if (!modalAprobarOpen) {
      setAprobarMedicoBuscar('');
    }
  }, [modalAprobarOpen]);

  useEffect(() => {
    if (!editarMedicamentoPickerOpen) return undefined;
    const onDown = (e) => {
      const root = editarMedicamentoPickerRef.current;
      if (root && !root.contains(e.target)) setEditarMedicamentoPickerOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [editarMedicamentoPickerOpen]);

  const bootstrapListLoading = pedidosLoading;
  const pendientesResultadoLabel = useMemo(() => {
    if (bootstrapListLoading) return 'Preparando…';
    const pedidos = filteredPendientes.length;
    const pacientes = pendientesAgrupadosPorPaciente.length;
    if (pedidos === 0) return '0 resultados';
    if (pacientes < pedidos) return `${pacientes} paciente${pacientes === 1 ? '' : 's'} · ${pedidos} pedido${pedidos === 1 ? '' : 's'}`;
    return `${pedidos} resultado${pedidos === 1 ? '' : 's'}`;
  }, [bootstrapListLoading, filteredPendientes.length, pendientesAgrupadosPorPaciente.length]);
  useEffect(() => {
    if (!editarMedicamentoPedidoId) return;
    const exists = data.pedidos.some((p) => Number(p.id) === Number(editarMedicamentoPedidoId));
    if (!exists) cancelarEditarMedicamento();
  }, [editarMedicamentoPedidoId, data.pedidos]);
  const selectedPedido = data.pedidos.find((p) => Number(p.id) === Number(selectedPedidoId)) || null;
  const historialForSelected = useMemo(() => {
    if (!selectedPedido) return [];
    const dni = selectedPedido.dni || selectedPedido.dni_normalizado || '';
    const staticHist = HISTORIAL[dni] || [];
    // derive history from loaded pedidos (exclude the currently selected one) and keep rich fields
    const fromDb = data.pedidos
      .filter(p => (p.dni === dni || String(p.dni_normalizado || '') === String(dni)) && p.id !== selectedPedido.id)
      .map(p => ({
        // keep original id so we can trace
        id: p.id,
        // Prefer generic + concentration in history for clinical readability.
        medicamento: toTitleCaseText(
          p.medicamento_generico
            ? `${p.medicamento_generico}${p.medicamento_concentracion ? ` ${p.medicamento_concentracion}` : ''}`
            : (p.medicamento || '')
        ),
        nombre_comercial: p.nombre_comercial || '',
        laboratorio: p.medicamento_laboratorio || '',
        posologia: p.posologia || '',
        periodo: p.periodo_meses ? `${p.periodo_meses} meses` : (p.fecha_inicio && p.fecha_fin ? `${p.fecha_inicio} - ${p.fecha_fin}` : ''),
        medico: p.medico || (medicosMapRef.current?.get ? (medicosMapRef.current.get(String(p.medico_id)) || (p.medico_id ? `Médico #${p.medico_id}` : '')) : (p.medico_id ? `Médico #${p.medico_id}` : '')),
        motivo_rechazo: p.motivo_rechazo || null,
        notas: p.notas || '',
        message_history: p.message_history || '',
        estado: p.estado || '',
        fecha_solicitud: p.fecha_solicitud || p.solicitado || '',
        acepta_recordatorios: !!p.acepta_recordatorios,
        cantidad_recetas: p.cantidad_recetas || 0,
        recetas_generadas: (() => {
          const key = String(p.id);
          if (key in (historialEmitidasByPedido || {})) {
            return Number(historialEmitidasByPedido[key]) || 0;
          }
          return resolveRecetasEmitidas(p);
        })()
      }));
    const combined = [...fromDb, ...staticHist];
    return combined.filter((h) => String(h.estado || '') !== 'pendiente_derivacion');
  }, [data.pedidos, selectedPedido, historialEmitidasByPedido]);

  useEffect(() => {
    // reset history controls when selecting another pedido
    setHistorySearch('');
    setHistoryPage(1);
    setHistoryItemsPerPage(5);
  }, [selectedPedidoId]);

  const filteredHistory = useMemo(() => {
    const q = String(historySearch || '').trim().toLowerCase();
    if (!q) return historialForSelected;
    return historialForSelected.filter(h => (
      String(h.medicamento || '').toLowerCase().includes(q) ||
      String(h.medico || '').toLowerCase().includes(q) ||
      String(h.periodo || '').toLowerCase().includes(q) ||
      String(h.notas || '').toLowerCase().includes(q) ||
      String(h.motivo_rechazo || '').toLowerCase().includes(q)
    ));
  }, [historialForSelected, historySearch]);

  const totalHistoryPages = Math.max(1, Math.ceil(filteredHistory.length / historyItemsPerPage));
  useEffect(() => { if (historyPage > totalHistoryPages) setHistoryPage(1); }, [filteredHistory.length, historyItemsPerPage]);
  const pageHistoryItems = useMemo(() => filteredHistory.slice((historyPage - 1) * historyItemsPerPage, historyPage * historyItemsPerPage), [filteredHistory, historyPage, historyItemsPerPage]);
  const historialPacientes = useMemo(() => {
    const byDni = new Map();
    (data.pedidos || []).forEach((p) => {
      const dni = String(p?.dni || p?.paciente_dni || p?.solicitante_dni || p?.dni_normalizado || '').trim();
      if (!dni) return;
      if (!byDni.has(dni)) {
        byDni.set(dni, {
          dni,
          paciente: p.paciente || 'Paciente',
          obra_social: p.obra_social || '',
        });
      }
    });
    return Array.from(byDni.values()).sort((a, b) => String(a.paciente).localeCompare(String(b.paciente), 'es'));
  }, [data.pedidos]);
  const filteredHistorialPacientes = useMemo(() => {
    const q = String(historialGlobalPacienteSearch || '').trim().toLowerCase();
    if (!q) return historialPacientes;
    return historialPacientes.filter((p) => (
      String(p.paciente || '').toLowerCase().includes(q) ||
      String(p.dni || '').toLowerCase().includes(q) ||
      String(p.obra_social || '').toLowerCase().includes(q)
    ));
  }, [historialPacientes, historialGlobalPacienteSearch]);
  const historialPedidoIdsForEmitidasFetch = useMemo(() => {
    const dniGlobal = String(historialGlobalDni || '').trim();
    const dniSidebar = (historyOpen && selectedPedido)
      ? String(selectedPedido.dni || selectedPedido.paciente_dni || selectedPedido.solicitante_dni || selectedPedido.dni_normalizado || '').trim()
      : '';
    const dni = dniGlobal || dniSidebar;
    if (!dni) return [];
    return (data.pedidos || [])
      .filter((p) => String(p?.dni || p?.paciente_dni || p?.solicitante_dni || p?.dni_normalizado || '').trim() === dni)
      .map((p) => Number(p.id))
      .filter((id) => Number.isFinite(id));
  }, [data.pedidos, historialGlobalDni, historyOpen, selectedPedido]);

  useEffect(() => {
    if (!historialPedidoIdsForEmitidasFetch.length) return;
    let cancelled = false;
    const missingIds = historialPedidoIdsForEmitidasFetch.filter((id) => !(String(id) in (historialEmitidasByPedido || {})));
    if (!missingIds.length) return;
    (async () => {
      for (const pid of missingIds) {
        try {
          const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${pid}/recetas`, { credentials: 'include' });
          if (!r.ok) continue;
          const jr = await r.json().catch(() => ({}));
          const persisted = Array.isArray(jr?.recetas) ? jr.recetas : (Array.isArray(jr) ? jr : []);
          const seen = new Set();
          const emittedCount = persisted.reduce((acc, rec) => {
            const estado = String(rec?.estado || '').toLowerCase();
            const hasFecha = Boolean(String(rec?.fecha_emitida || '').trim());
            const nroExt = String(rec?.nro_receta_externo || rec?.nro_externo || '').trim();
            const hasNro = Boolean(nroExt);
            const isEmitida = estado === 'emitida_sistema_externo' || estado === 'emitida' || estado === 'confirmada_dispensa' || hasFecha || hasNro;
            if (!isEmitida) return acc;
            const recId = String(rec?.id || rec?.receta_id || '').trim();
            const recNumero = String(rec?.numero_receta || rec?.numero || '').trim();
            const key = hasNro ? `nro:${nroExt.toLowerCase()}` : (recId ? `id:${recId}` : `num:${recNumero || 's/n'}`);
            if (seen.has(key)) return acc;
            seen.add(key);
            return acc + 1;
          }, 0);
          if (cancelled) return;
          setHistorialEmitidasByPedido((prev) => ({ ...prev, [String(pid)]: emittedCount }));
        } catch (_) {
          // ignore per-pedido fetch errors
        }
      }
    })();
    return () => { cancelled = true; };
  }, [historialPedidoIdsForEmitidasFetch, historialEmitidasByPedido]);
  const emittedRecetasByPedidoId = useMemo(() => {
    const out = new Map();
    const seenByPedido = new Map();
    (data.recetas || []).forEach((r) => {
      const pid = Number(r?.pedido_id ?? r?.id_pedido ?? r?.pedidoId);
      if (!Number.isFinite(pid)) return;
      const estado = String(r?.estado || '').toLowerCase();
      const hasFechaEmitida = Boolean(String(r?.fecha_emitida || '').trim());
      const nroExterno = String(r?.nro_externo || r?.nroExterno || '').trim();
      const hasNroExterno = Boolean(nroExterno);
      const isEmitida = (
        estado === 'emitida_sistema_externo'
        || estado === 'emitida'
        || hasFechaEmitida
        || hasNroExterno
      );
      if (!isEmitida) return;
      const recetaId = String(r?.id ?? '').trim();
      const recetaNumero = String(r?.numero ?? '').trim();
      const key = (
        hasNroExterno
          ? `nro:${nroExterno.toLowerCase()}`
          : (recetaId ? `id:${recetaId}` : `num:${recetaNumero || 's/n'}`)
      );
      if (!seenByPedido.has(pid)) seenByPedido.set(pid, new Set());
      const seen = seenByPedido.get(pid);
      if (seen.has(key)) return;
      seen.add(key);
      out.set(pid, (out.get(pid) || 0) + 1);
    });
    return out;
  }, [data.recetas]);

  const historialGlobalItems = useMemo(() => {
    if (!historialGlobalDni) return [];
    const rows = (data.pedidos || [])
      .filter((p) => String(p?.dni || p?.paciente_dni || p?.solicitante_dni || p?.dni_normalizado || '').trim() === String(historialGlobalDni))
      .filter((p) => String(p.estado || '') !== 'pendiente_derivacion')
      .map((p) => ({
        id: p.id,
        medicamento: toTitleCaseText(
          p.medicamento_generico
            ? `${p.medicamento_generico}${p.medicamento_concentracion ? ` ${p.medicamento_concentracion}` : ''}`
            : (p.medicamento || '')
        ),
        nombre_comercial: p.nombre_comercial || '',
        laboratorio: p.medicamento_laboratorio || '',
        posologia: p.posologia || '',
        periodo: p.periodo_meses ? `${p.periodo_meses} meses` : '',
        medico: p.medico || (medicosMapRef.current?.get ? (medicosMapRef.current.get(String(p.medico_id)) || (p.medico_id ? `Médico #${p.medico_id}` : '')) : ''),
        motivo_rechazo: p.motivo_rechazo || null,
        notas: p.notas || '',
        estado: p.estado || '',
        fecha_solicitud: p.fecha_solicitud || p.solicitado || '',
        cantidad_recetas: p.cantidad_recetas || 0,
        recetas_generadas: (() => {
          const remotePersisted = Number(historialEmitidasByPedido[String(p.id)]);
          if (Number.isFinite(remotePersisted)) return remotePersisted;
          const localEmitidas = emittedRecetasByPedidoId.get(Number(p.id)) || 0;
          return localEmitidas > 0 ? localEmitidas : resolveRecetasEmitidas(p);
        })()
      }));
    return rows.sort((a, b) => String(b.fecha_solicitud || '').localeCompare(String(a.fecha_solicitud || '')));
  }, [data.pedidos, historialGlobalDni, emittedRecetasByPedidoId, historialEmitidasByPedido]);
  const filteredHistorialGlobalItems = useMemo(() => {
    const q = String(historialGlobalSearch || '').trim().toLowerCase();
    const toMs = (v) => {
      const iso = normalizeToIsoDate(v);
      if (!iso) return 0;
      const d = new Date(`${iso}T00:00:00`);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    const desdeMs = toMs(historialGlobalDesde);
    const hastaMs = toMs(historialGlobalHasta);

    let rows = historialGlobalItems.filter((h) => (
      String(h.medicamento || '').toLowerCase().includes(q) ||
      String(h.nombre_comercial || '').toLowerCase().includes(q) ||
      String(h.laboratorio || '').toLowerCase().includes(q) ||
      String(h.medico || '').toLowerCase().includes(q) ||
      String(h.estado || '').toLowerCase().includes(q) ||
      String(h.notas || '').toLowerCase().includes(q) ||
      String(h.motivo_rechazo || '').toLowerCase().includes(q)
    ));
    if (historialGlobalEstado !== 'todos') rows = rows.filter((h) => String(h.estado) === String(historialGlobalEstado));
    if (desdeMs || hastaMs) {
      rows = rows.filter((h) => {
        const ts = toMs(h.fecha_solicitud);
        if (!ts) return false;
        if (desdeMs && ts < desdeMs) return false;
        if (hastaMs && ts > hastaMs) return false;
        return true;
      });
    }
    rows = rows.slice().sort((a, b) => {
      const aTs = toMs(a.fecha_solicitud);
      const bTs = toMs(b.fecha_solicitud);
      if (historialGlobalOrder === 'fecha_asc') return aTs - bTs;
      return bTs - aTs;
    });
    return rows;
  }, [historialGlobalItems, historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalEstado, historialGlobalOrder]);
  const totalHistorialGlobalPages = Math.max(1, Math.ceil(filteredHistorialGlobalItems.length / historialGlobalItemsPerPage));
  useEffect(() => {
    if (historialGlobalPage > totalHistorialGlobalPages) setHistorialGlobalPage(1);
  }, [historialGlobalPage, totalHistorialGlobalPages]);
  useEffect(() => {
    setHistorialGlobalPage(1);
  }, [historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalEstado, historialGlobalOrder, historialGlobalDni, historialGlobalItemsPerPage]);
  const pageHistorialGlobalItems = useMemo(() => (
    filteredHistorialGlobalItems.slice((historialGlobalPage - 1) * historialGlobalItemsPerPage, historialGlobalPage * historialGlobalItemsPerPage)
  ), [filteredHistorialGlobalItems, historialGlobalPage, historialGlobalItemsPerPage]);
  const verRecetaPedido = useMemo(() => (
    (data.pedidos || []).find((p) => Number(p.id) === Number(verRecetaPedidoId)) || null
  ), [data.pedidos, verRecetaPedidoId]);
  const verRecetaRecetas = useMemo(() => (
    (data.recetas || []).filter((r) => Number(r.pedido_id) === Number(verRecetaPedidoId))
  ), [data.recetas, verRecetaPedidoId]);
  const verRecetaMedicoLabel = useMemo(() => {
    if (!verRecetaPedido) return '—';
    const byId = mergedMedicos.find((m) => String(m.id) === String(verRecetaPedido.medico_id));
    const byName = mergedMedicos.find((m) => String(m.nombre || '').toLowerCase() === String(verRecetaPedido.medico || '').toLowerCase());
    const medicoRef = byId || byName || null;
    const nombre = medicoRef?.nombre || verRecetaPedido?.medico || '';
    const genero = medicoRef?.genero || null;
    return formatDoctorDisplayName(nombre, genero);
  }, [verRecetaPedido, mergedMedicos]);
  const verRecetaEmitidas = useMemo(() => {
    if (!verRecetaPedido) return 0;
    const remotePersisted = Number(historialEmitidasByPedido[String(verRecetaPedido.id)]);
    if (Number.isFinite(remotePersisted)) return remotePersisted;
    const localEmitidas = emittedRecetasByPedidoId.get(Number(verRecetaPedido.id)) || 0;
    return localEmitidas > 0 ? localEmitidas : resolveRecetasEmitidas(verRecetaPedido);
  }, [verRecetaPedido, emittedRecetasByPedidoId, historialEmitidasByPedido]);
  const verRecetaFechaFinLabel = useMemo(() => {
    if (!verRecetaPedido) return '—';
    const fechaFinRaw = normalizeToIsoDate(verRecetaPedido.fecha_fin) || verRecetaPedido.fecha_fin;
    if (fechaFinRaw) return formatSolicitud(fechaFinRaw);
    const fechaInicioRaw = normalizeToIsoDate(verRecetaPedido.fecha_inicio) || verRecetaPedido.fecha_inicio;
    const meses = Number(verRecetaPedido.periodo_meses || verRecetaPedido.cantidad_recetas || 0);
    if (!fechaInicioRaw || !Number.isFinite(meses) || meses <= 0) return '—';
    const fechaFinCalc = new Date(fechaInicioRaw);
    if (Number.isNaN(fechaFinCalc.getTime())) return '—';
    fechaFinCalc.setMonth(fechaFinCalc.getMonth() + meses);
    return formatSolicitud(fechaFinCalc.toISOString().slice(0, 10));
  }, [verRecetaPedido]);
  useEffect(() => {
    if (!modalVerRecetaOpen || !verRecetaPedidoId) return;
    const key = String(verRecetaPedidoId);
    if (Array.isArray(historialRecetasByPedido[key])) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${verRecetaPedidoId}/recetas`, { credentials: 'include' });
        if (!r.ok) return;
        const jr = await r.json().catch(() => ({}));
        const persisted = Array.isArray(jr?.recetas) ? jr.recetas : (Array.isArray(jr) ? jr : []);
        const normalized = persisted.map((rec, idx) => ({
          id: rec?.id || rec?.receta_id || `${verRecetaPedidoId}-${idx}`,
          numero: rec?.numero_receta || rec?.numero || null,
          nro_externo: rec?.nro_receta_externo || rec?.nro_externo || '',
          fecha_prevista: rec?.fecha_prevista || null,
          fecha_emitida: rec?.fecha_emitida ? new Date(rec.fecha_emitida).toLocaleDateString('es-AR') : null,
          estado: rec?.estado || (rec?.fecha_emitida || rec?.nro_receta_externo ? 'emitida_sistema_externo' : 'pendiente')
        }));
        if (cancelled) return;
        setHistorialRecetasByPedido((prev) => ({ ...prev, [key]: normalized }));
      } catch (_) {
        // ignore fetch errors for detalle recetas
      }
    })();
    return () => { cancelled = true; };
  }, [modalVerRecetaOpen, verRecetaPedidoId, historialRecetasByPedido]);
  const verRecetaEmitidasDetalle = useMemo(() => {
    const source = (historialRecetasByPedido[String(verRecetaPedidoId)] || verRecetaRecetas || []);
    const seen = new Set();
    return source.filter((r) => {
      const estado = String(r?.estado || '').toLowerCase();
      const hasFecha = Boolean(String(r?.fecha_emitida || '').trim());
      const nroExterno = String(r?.nro_externo || r?.nroExterno || '').trim();
      const hasNro = Boolean(nroExterno);
      const isEmitida = estado === 'emitida_sistema_externo' || estado === 'emitida' || estado === 'confirmada_dispensa' || hasFecha || hasNro;
      if (!isEmitida) return false;
      const recId = String(r?.id || '').trim();
      const recNumero = String(r?.numero || '').trim();
      const key = hasNro ? `nro:${nroExterno.toLowerCase()}` : (recId ? `id:${recId}` : `num:${recNumero || 's/n'}`);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [historialRecetasByPedido, verRecetaPedidoId, verRecetaRecetas]);
  const verRecetaPrintRows = useMemo(() => (
    (verRecetaEmitidasDetalle || []).map((r) => ({
      numero: r?.numero || r?.id || '-',
      fecha_prevista: r?.fecha_prevista || '',
      fecha_emitida: r?.fecha_emitida || 'Pendiente',
      nro_externo: r?.nro_externo || r?.nroExterno || '',
      estado: (() => {
        const s = String(r?.estado || '').toLowerCase();
        if (s === 'emitida_sistema_externo' || s === 'emitida') return 'Emitida';
        if (s === 'confirmada_dispensa') return 'Confirmada dispensa';
        return r?.estado || 'Emitida';
      })()
    }))
  ), [verRecetaEmitidasDetalle]);

  const openHistorialGlobal = () => {
    setHistorialGlobalDni('');
    setHistorialGlobalSearch('');
    setHistorialGlobalPacienteSearch('');
    setHistorialGlobalDesde('');
    setHistorialGlobalHasta('');
    setHistorialGlobalEstado('todos');
    setHistorialGlobalOrder('fecha_desc');
    setHistorialGlobalPage(1);
    setModalHistorialGlobalOpen(true);
  };

  const abrirVerRecetaDesdeHistorial = (item) => {
    setVerRecetaPedidoId(item?.id ?? null);
    setModalHistorialGlobalOpen(false);
    setModalVerRecetaOpen(true);
  };
  const cerrarVerReceta = () => {
    setModalVerRecetaOpen(false);
    setModalHistorialGlobalOpen(true);
  };
  const imprimirVistaReceta = async () => {
    if (!verRecetaPedido) return;
    const pedidoActual = (data.pedidos || []).find((p) => Number(p.id) === Number(verRecetaPedido.id)) || verRecetaPedido;
    let printPedido = { ...pedidoActual };
    const isLoadingMedicamento = String(printPedido?.medicamento || '').toLowerCase().includes('cargando medicamento');
    const medId = Number(printPedido?.medicamento_id || 0);
    if (isLoadingMedicamento && medId) {
      try {
        const mr = await fetch(`${API_BASE_URL}/api/asociacion-recetas/medicamento/${medId}`, { credentials: 'include' });
        if (mr.ok) {
          const jm = await mr.json();
          printPedido = {
            ...printPedido,
            medicamento: jm.nombre_comercial || jm.nombre_generico || printPedido.medicamento,
            medicamento_generico: jm.nombre_generico || printPedido.medicamento_generico,
            medicamento_concentracion: jm.concentracion || printPedido.medicamento_concentracion,
            medicamento_laboratorio: jm.laboratorio || printPedido.medicamento_laboratorio,
            nombre_comercial: jm.nombre_comercial || printPedido.nombre_comercial
          };
        }
      } catch (_) {
        // ignore print-time medicamento lookup errors
      }
    }
    const clean = (v) => {
      const s = String(v ?? '').trim();
      if (!s) return '';
      const low = s.toLowerCase();
      return (low === '0' || low === 'null' || low === 'undefined') ? '' : s;
    };
    const estadoPrintableMap = {
      pendiente_aprobacion_medica: 'Pendiente médico',
      aprobado: 'Aprobado',
      rechazado: 'Rechazado',
      emitida_sistema_externo: 'Emitida',
      emitida: 'Emitida',
      confirmada_dispensa: 'Confirmada dispensa'
    };
    const estadoPrintable = estadoPrintableMap[String(printPedido?.estado || '').toLowerCase()] || (printPedido?.estado || '-');
    const printFechaFin = (() => {
      const fechaFinRaw = normalizeToIsoDate(printPedido?.fecha_fin) || printPedido?.fecha_fin;
      if (fechaFinRaw) return formatSolicitud(fechaFinRaw);
      const fechaInicioRaw = normalizeToIsoDate(printPedido?.fecha_inicio) || printPedido?.fecha_inicio;
      const meses = Number(printPedido?.periodo_meses || printPedido?.cantidad_recetas || 0);
      if (!fechaInicioRaw || !Number.isFinite(meses) || meses <= 0) return '-';
      const fechaFinCalc = new Date(fechaInicioRaw);
      if (Number.isNaN(fechaFinCalc.getTime())) return '-';
      fechaFinCalc.setMonth(fechaFinCalc.getMonth() + meses);
      return formatSolicitud(fechaFinCalc.toISOString().slice(0, 10));
    })();
    const medPrintable = toTitleCaseText(
      printPedido?.medicamento_generico
        ? `${printPedido?.medicamento_generico} ${printPedido?.medicamento_concentracion || ''}`
        : printPedido?.medicamento
    ) || '-';
    const marcaPrintable = [clean(printPedido?.nombre_comercial), clean(printPedido?.medicamento_laboratorio)].filter(Boolean).join(' · ') || 'Sin marca comercial';
    const recetaRows = (verRecetaPrintRows || []).map((r) => (
      `<tr>
        <td>${r.numero || r.id || '-'}</td>
        <td>${r.fecha_emitida || 'Pendiente'}</td>
        <td>${r.nro_externo || ''}</td>
        <td>${r.estado || 'pendiente'}</td>
      </tr>`
    )).join('');
    const popup = window.open('', '_blank', 'width=980,height=760');
    if (!popup) return;
    popup.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Tratamiento aprobado #${verRecetaPedido.id || ''}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 28px; color: #0f172a; }
      h1 { margin: 0 0 8px; font-size: 22px; }
      .muted { color: #475569; font-size: 12px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 14px; }
      .box { border: 1px solid #dbe3ef; border-radius: 10px; padding: 10px; }
      .label { font-size: 10px; letter-spacing: .06em; color: #64748b; text-transform: uppercase; font-weight: 700; }
      .value { margin-top: 6px; font-size: 13px; }
      table { width: 100%; border-collapse: collapse; margin-top: 12px; }
      th, td { border: 1px solid #dbe3ef; padding: 8px; text-align: left; font-size: 12px; }
      th { background: #f8fafc; }
    </style>
  </head>
  <body>
    <h1>Tratamiento aprobado</h1>
    <div class="muted">Fecha de solicitud: ${printPedido.fecha_solicitud ? formatSolicitud(printPedido.fecha_solicitud) : '-'}</div>
    <div class="grid">
      <div class="box"><div class="label">Paciente</div><div class="value">${printPedido.paciente || '-'}</div><div class="muted">DNI ${printPedido.dni || printPedido.dni_normalizado || '—'} · OS ${printPedido.obra_social || '—'}</div></div>
      <div class="box"><div class="label">Datos clínicos</div><div class="value">Médico: ${verRecetaMedicoLabel || '-'}</div><div class="muted">Posología: ${printPedido.posologia || '-'} · Cantidad: ${printPedido.cantidad_recetas || 0} · Emitidas: ${verRecetaEmitidas}</div></div>
      <div class="box"><div class="label">Medicamento</div><div class="value">${medPrintable}</div><div class="muted">${marcaPrintable}</div></div>
      <div class="box"><div class="label">Vigencia</div><div class="value">Inicio: ${printPedido.fecha_inicio ? formatSolicitud(normalizeToIsoDate(printPedido.fecha_inicio) || printPedido.fecha_inicio) : '-'}</div><div class="value">Fin: ${printFechaFin}</div><div class="muted">Estado: ${estadoPrintable}</div></div>
    </div>
    <h2 style="margin-top:18px;font-size:16px;">Recetas asociadas</h2>
    ${verRecetaPrintRows.length ? `<table><thead><tr><th>#</th><th>Emitida</th><th>N° externo</th><th>Estado</th></tr></thead><tbody>${recetaRows}</tbody></table>` : '<div class="muted">Todavía no hay recetas emitidas para este pedido.</div>'}
    ${printPedido.notas ? `<h2 style="margin-top:18px;font-size:16px;">Notas</h2><div class="box">${printPedido.notas}</div>` : ''}
  </body>
</html>`);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  // actions
  function abrirAprobar(id) {
    const p = data.pedidos.find(x => x.id === id) || {};
    const sinRecordatorios = !p.acepta_recordatorios;
    setSelectedPedidoId(id);
    setAprobandoPedidoId(id); // Marcar card seleccionada
    setAprobarPosologia(p.posologia || '');
    // normalize to YYYY-MM-DD for DatePicker
    const iso = normalizeToIsoDate(p.fecha_inicio || p.fecha_solicitud);
    setAprobarFecha(iso || new Date().toISOString().split('T')[0]);
    setAprobarMeses(p.periodo_meses || p.periodo || (sinRecordatorios ? 1 : 6));
    const rawCantidad = p.cantidad_recetas;
    const cantidadDefault = String(
      rawCantidad != null && rawCantidad !== ''
        ? rawCantidad
        : (sinRecordatorios ? 1 : 6)
    );
    setAprobarCantidad(cantidadDefault);
    setAprobarDosificacion(createDosificacionStateFromPedido(p));
    const flujoDefaults = resolveAprobacionFlujoDefaults(
      { ...p, cantidad_recetas: Number(cantidadDefault) || 1 },
      { isAdmin, linkedMedicoId }
    );
    setAprobarEmisorTratamiento(
      p.emisor_tratamiento || flujoDefaults.emisorTratamiento
    );
    const webhookDefaults = resolveAprobacionWebhookDefaults(cantidadDefault);
    setAprobarWebhookAlta(
      p.estado === 'aprobado'
        ? coerceWebhookFlag(p.disparar_webhook_alta_tratamiento, webhookDefaults.webhookAlta)
        : webhookDefaults.webhookAlta
    );
    setAprobarWebhookEmitir(
      p.estado === 'aprobado'
        ? coerceWebhookFlag(p.disparar_webhook_emitir_receta, webhookDefaults.webhookEmitir)
        : webhookDefaults.webhookEmitir
    );
    setAprobarMedicoSeleccion('');
    setAprobarMedicoBuscar('');
    const emisorDefault = p.emisor_tratamiento || flujoDefaults.emisorTratamiento;
    const adminDefault = p.administrativo_id
      ? String(p.administrativo_id)
      : (emisorDefault === EMISOR_TRATAMIENTO_ADMIN && linkedAdministrativoId
        ? String(linkedAdministrativoId)
        : '');
    setAprobarAdministrativoSeleccion(adminDefault);
    setAprobarWizardEpoch((n) => n + 1);
    setModalAprobarOpen(true);
  }

  function handleAprobarEmisorChange(value) {
    setAprobarEmisorTratamiento(value);
    if (value === EMISOR_TRATAMIENTO_ADMIN) {
      if (!aprobarAdministrativoSeleccion && linkedAdministrativoId) {
        setAprobarAdministrativoSeleccion(String(linkedAdministrativoId));
      }
    } else {
      setAprobarAdministrativoSeleccion('');
    }
  }

  function handleAprobarCantidadChange(value) {
    setAprobarCantidad(value);
    const { webhookAlta, webhookEmitir } = resolveAprobacionWebhookDefaults(value);
    setAprobarWebhookAlta(webhookAlta);
    setAprobarWebhookEmitir(webhookEmitir);
  }

  function abrirEditarMedicamento(pedido) {
    if (!pedido?.id) return;
    const selected = Number.isFinite(Number(pedido.medicamento_id))
      ? {
          id: Number(pedido.medicamento_id),
          nombre_generico: pedido.medicamento_generico || pedido.medicamento || '',
          nombre_comercial: pedido.nombre_comercial || '',
          laboratorio: pedido.medicamento_laboratorio || '',
          concentracion: pedido.medicamento_concentracion || '',
          presentacion: pedido.medicamento_presentacion || ''
        }
      : null;
    setEditarMedicamentoPedidoId(pedido.id);
    setEditarMedicamentoSearch('');
    setEditarMedicamentos([]);
    setEditarMedicamentoPickerOpen(false);
    setEditarMedicamentoChosen(selected);
  }

  function cancelarEditarMedicamento() {
    setEditarMedicamentoPedidoId(null);
    setEditarMedicamentoSearch('');
    setEditarMedicamentos([]);
    setEditarMedicamentoPickerOpen(false);
    setEditarMedicamentoChosen(null);
    setEditarMedicamentoSaving(false);
  }

  async function guardarEditarMedicamento() {
    const pedidoId = Number(editarMedicamentoPedidoId);
    const medicamentoId = Number(editarMedicamentoChosen?.id || 0);
    if (!Number.isFinite(pedidoId) || !Number.isFinite(medicamentoId) || medicamentoId <= 0) {
      toast('Seleccioná un medicamento válido', 'error');
      return;
    }
    setEditarMedicamentoSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${pedidoId}/medicamento`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ medicamento_id: medicamentoId })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const json = await res.json().catch(() => ({}));
      const updatedPedido = json?.pedido || {};
      const med = json?.medicamento || editarMedicamentoChosen || {};
      const medDisplay = toTitleCaseText(
        med?.nombre_generico
          ? `${med.nombre_generico}${med?.concentracion ? ` ${med.concentracion}` : ''}`
          : (med?.nombre || med?.nombre_comercial || '')
      ) || toTitleCaseText(editarMedicamentoChosen?.nombre_generico || editarMedicamentoChosen?.nombre_comercial || '');

      setData((d) => ({
        ...d,
        pedidos: d.pedidos.map((p) => (
          Number(p.id) === pedidoId
            ? {
                ...p,
                medicamento_id: updatedPedido.medicamento_id || medicamentoId,
                medicamento: medDisplay || p.medicamento,
                medicamento_generico: med?.nombre_generico || p.medicamento_generico || '',
                medicamento_concentracion: med?.concentracion || p.medicamento_concentracion || '',
                medicamento_presentacion: med?.presentacion || p.medicamento_presentacion || '',
                medicamento_laboratorio: med?.laboratorio || p.medicamento_laboratorio || '',
                nombre_comercial: med?.nombre_comercial || p.nombre_comercial || ''
              }
            : p
        ))
      }));

      toast({ msg: 'Medicamento actualizado', paciente: data.pedidos.find((p) => Number(p.id) === pedidoId)?.paciente || '' }, 'success');
      cancelarEditarMedicamento();
    } catch (e) {
      console.error('Error actualizando medicamento de pedido:', e);
      toast(`Error al guardar medicamento: ${e.message || ''}`, 'error');
    } finally {
      setEditarMedicamentoSaving(false);
    }
  }

  async function confirmarAprobacion(wizardPayload = {}) {
    const webhookAltaFlag = wizardPayload.webhookAlta ?? aprobarWebhookAlta;
    const webhookEmitirFlag = wizardPayload.webhookEmitir ?? aprobarWebhookEmitir;
    const emisorTratamientoFlag = wizardPayload.emisorTratamiento ?? aprobarEmisorTratamiento;
    const administrativoSeleccionFlag = wizardPayload.administrativoSeleccion ?? aprobarAdministrativoSeleccion;

    // show toastr with summary and indicate missing fields using 'Seleccionar ...'
    const pedidoAprobar = data.pedidos.find(p => p.id === selectedPedidoId) || {};
    const permiteElegirMedico = puedeAdminDerivarMedicoEnAprobacion(pedidoAprobar, isAdmin);
    const medicosParaAprobacion = medicosDerivacion.length ? medicosDerivacion : mergedMedicos;

    const {
      medicoAsignadoId,
      medicoAsignadoNombre,
      selectedMed: selectedMedResolved
    } = resolveMedicoTratanteEnAprobacion(pedidoAprobar, {
      isAdmin,
      linkedMedicoId,
      medicoSeleccionId: aprobarMedicoSeleccion,
      medicos: medicosParaAprobacion,
      user
    });

    const {
      administrativoAsignadoId
    } = resolveAdministrativoEmisionEnAprobacion({
      administrativoSeleccionId: administrativoSeleccionFlag,
      administrativos: administrativosEmision,
      linkedAdministrativoId,
      user
    });

    const missing = [];
    if (!aprobarPosologia.trim()) missing.push('Posología');
    if (!aprobarCantidad) missing.push('Cantidad de recetas');
    // Emisión administrativa: ya no requiere elegir una persona específica.
    // Queda genérica (administrativo_id = null) y la ve todo el rol administrativo.
    if (permiteElegirMedico) {
      if (!aprobarMedicoSeleccion.trim() || !medicosParaAprobacion.find((m) => String(m.id) === String(aprobarMedicoSeleccion))) {
        missing.push('Médico asignado');
      }
    } else if (pedidoRequiereSeleccionMedicoEnAprobacion(pedidoAprobar) && !medicoAsignadoId) {
      missing.push('Médico asignado');
    } else if (!pedidoRequiereSeleccionMedicoEnAprobacion(pedidoAprobar) && !medicoAsignadoId && !medicoAsignadoNombre) {
      missing.push('Médico asignado');
    }
    if (missing.length) {
      const fmtDate = (iso) => {
        try {
          const d = parseLocalDate(iso);
          if (!d) return iso || '';
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const yy = d.getFullYear();
          return `${dd}/${mm}/${yy}`;
        } catch { return iso || ''; }
      };
      const safePosologia = aprobarPosologia.trim() || '<span style="color:#7C98B6">Seleccionar posología</span>';
      const safeCantidad = aprobarCantidad || '<span style="color:#7C98B6">Seleccionar cantidad</span>';
      const safeMedico = medicoAsignadoNombre || medicoAsignadoId
        || (permiteElegirMedico
          ? '<span style="color:#7C98B6">Seleccionar médico</span>'
          : '<span style="color:#7C98B6">Pedido sin médico derivado</span>');
      const html = `
        <div style="text-align:left;font-family:Lexend, sans-serif;color:#516F90;line-height:1.4">
          <p style="margin:0 0 6px 0;color:#253342;font-weight:700;font-size:1rem">Completá los datos del tratamiento</p>
          <div style="font-size:13px;color:#33475B">
            <div><strong>Período (meses):</strong> ${Number(aprobarMeses) || 6}</div>
            <div><strong>Fecha de inicio:</strong> ${fmtDate(aprobarFecha)}</div>
            <div><strong>Posología:</strong> ${safePosologia}</div>
            <div><strong>Cantidad:</strong> ${safeCantidad} recetas</div>
            <div><strong>Médico:</strong> ${safeMedico}</div>
          </div>
        </div>
      `;
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        html,
        showConfirmButton: false,
        timer: 4500,
        timerProgressBar: true,
        icon: 'error',
        customClass: { popup: 'swal-toast-crm' },
        background: '#fff'
      });
      return;
    }
    const whatsappItems = [];
    if (webhookAltaFlag) whatsappItems.push('WhatsApp · Alta tratamiento');
    if (webhookEmitirFlag) whatsappItems.push('WhatsApp · Emitir receta (al registrar emisión)');

    // Cerrar el wizard antes del Swal: su backdrop (z-index 3500) tapaba el diálogo de confirmación.
    setModalAprobarOpen(false);
    const confirmResult = await fireConfirmDialog({
      title: 'Aprobar pedido',
      html: buildCrmSwalBody({
        lead: 'Se aprobará el tratamiento y se generarán las recetas.',
        items: [
          'El paciente podrá continuar el circuito',
          whatsappItems.length
            ? `Se disparará: ${whatsappItems.join(' y ')}`
            : 'Sin avisos automáticos por WhatsApp'
        ]
      }),
      confirmButtonText: 'Aprobar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirmResult.isConfirmed) {
      setModalAprobarOpen(true);
      setAprobandoPedidoId(selectedPedidoId);
      return;
    }
    setAprobandoPedidoId(null);
    (async () => {
      setRechazoSubmitting(true);
      try {
        const selectedMed = selectedMedResolved
          || medicosParaAprobacion.find(m => String(m.id) === medicoAsignadoId)
          || medicosParaAprobacion.find(m => String(m.nombre || '').toLowerCase() === String(medicoAsignadoNombre || '').toLowerCase())
          || (medicoAsignadoNombre ? { id: medicoAsignadoId, nombre: medicoAsignadoNombre } : null);
        const medicoIdAsNumber = Number(medicoAsignadoId);
        const hasNumericMedicoId = Number.isFinite(medicoIdAsNumber) && medicoIdAsNumber > 0;
        const dosificacionCalculo = calcularDosificacion({
          dosisPorToma: aprobarDosificacion.dosisPorToma,
          tomasPorDia: aprobarDosificacion.tomasPorDia,
          unidadesPorReceta: aprobarDosificacion.unidadesPorReceta,
          cantidadRecetas: Number(aprobarCantidad) || 1,
          fechaInicio: aprobarFecha
        });
        const dosificacionApi = serializeDosificacionForApi(aprobarDosificacion, dosificacionCalculo);
        const body = {
          periodo_meses: Number(aprobarMeses) || null,
          fecha_inicio: aprobarFecha || null,
          posologia: aprobarPosologia || null,
          cantidad_recetas: Number(aprobarCantidad) || null,
          ...dosificacionApi,
          medico_id: hasNumericMedicoId ? medicoIdAsNumber : null,
          medico: selectedMed ? selectedMed.nombre : null,
          medico_especialidad: selectedMed ? (selectedMed.especialidad || null) : null,
          medico_matricula: selectedMed ? (selectedMed.matricula || null) : null,
          emisor_tratamiento: emisorTratamientoFlag,
          administrativo_id: emisorTratamientoFlag === EMISOR_TRATAMIENTO_ADMIN
            ? (Number(administrativoAsignadoId) || null)
            : null,
          disparar_webhook_alta_tratamiento: webhookAltaFlag ? 1 : 0,
          disparar_webhook_emitir_receta: webhookEmitirFlag ? 1 : 0
        };
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${selectedPedidoId}/aprobar`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.message || json.error || `HTTP ${res.status}`);
        const updated = json.pedido || json;
        // if backend returned medico object, ensure local medicos list/map is updated
        if (json.medico) {
          try {
            const normalized = normalizeMedicosFromApi({ medicos: [json.medico] });
            const m = normalized[0];
            if (!m) throw new Error('médico inválido');
            setMedicosList((prev) => {
              const map = new Map();
              if (Array.isArray(prev)) prev.forEach(x => map.set(String(x.id), x));
              map.set(String(m.id), m);
              return Array.from(map.values());
            });
            if (medicosMapRef.current) medicosMapRef.current.set(String(m.id), m.nombre);
          } catch (e) { /* ignore */ }
        }

        setData((d) => {
          const pedidos = d.pedidos.map(p => p.id === selectedPedidoId ? { ...p, estado: updated.estado || 'aprobado', medico: (json.medico ? json.medico.nombre : (updated.medico_id ? (medicosMapRef.current.get ? medicosMapRef.current.get(String(updated.medico_id)) : null) : medicoAsignadoNombre)), periodo_meses: updated.periodo_meses || Number(aprobarMeses), fecha_inicio: updated.fecha_inicio || aprobarFecha, posologia: updated.posologia || aprobarPosologia, cantidad_recetas: updated.cantidad_recetas || Number(aprobarCantidad), dosis_por_toma: updated.dosis_por_toma ?? dosificacionApi.dosis_por_toma, unidad_dosis: updated.unidad_dosis ?? dosificacionApi.unidad_dosis, tomas_por_dia: updated.tomas_por_dia ?? dosificacionApi.tomas_por_dia, unidades_por_receta: updated.unidades_por_receta ?? dosificacionApi.unidades_por_receta, consumo_diario_unidades: updated.consumo_diario_unidades ?? dosificacionApi.consumo_diario_unidades, duracion_receta_dias: updated.duracion_receta_dias ?? dosificacionApi.duracion_receta_dias, fecha_fin_receta: updated.fecha_fin_receta ?? dosificacionApi.fecha_fin_receta, emisor_tratamiento: updated.emisor_tratamiento || aprobarEmisorTratamiento, disparar_webhook_alta_tratamiento: updated.disparar_webhook_alta_tratamiento ?? aprobarWebhookAlta, disparar_webhook_emitir_receta: updated.disparar_webhook_emitir_receta ?? aprobarWebhookEmitir } : p);
          const pedido = pedidos.find(p => p.id === selectedPedidoId);
          const recetas = [...d.recetas];
          const fechaInicio = new Date(pedido.fecha_inicio || aprobarFecha);
          const cantidadRecetas = Number(pedido.cantidad_recetas || aprobarCantidad) || 0;
          for (let i = 0; i < cantidadRecetas; i += 1) {
            const numero = i + 1;
            const fechaRec = new Date(fechaInicio);
            fechaRec.setMonth(fechaRec.getMonth() + i);
            recetas.push({
              id: `${selectedPedidoId}-${numero}`,
              pedido_id: selectedPedidoId,
              paciente: pedido.paciente,
              medicamento: pedido.medicamento,
              numero,
              fecha_prevista: fechaRec.toISOString().split('T')[0],
              fecha_emitida: null,
              estado: 'pendiente',
              nro_externo: null
            });
          }
          return { ...d, pedidos, recetas };
        });
        setModalAprobarOpen(false);
        setAprobandoPedidoId(null);
        // cerrar historial y deseleccionar el pedido tras la aprobación
        setHistoryOpen(false);
        setSelectedPedidoId(null);
        setHistoryPage(1);
        const wh = json?.webhook || {};
        const altaWh = wh.alta_tratamiento || {};
        const emitirWh = wh.emitir_receta || {};
        const webhookOk = !wh || (!altaWh.solicitado || altaWh.enviado === true);
        const webhookParts = [];
        if (altaWh.solicitado) {
          webhookParts.push(altaWh.enviado
            ? 'Alta tratamiento: enviado'
            : `Alta tratamiento: falló${altaWh.error ? ` (${altaWh.error})` : ''}`);
        }
        if (emitirWh.solicitado || emitirWh.programado) {
          webhookParts.push('Emitir receta: pendiente al registrar emisión');
        }
        const webhookMsg = webhookParts.length
          ? webhookParts.join(' · ')
          : 'Aprobado sin avisos automáticos al paciente.';
        Swal.fire({
          toast: true,
          position: 'bottom-end',
          html: `
            <div style="font-weight:700;color:#253342">Tratamiento aprobado</div>
            <div style="font-size:13px;color:#33475B;margin-top:6px">${webhookMsg}</div>
            <div style="margin-top:8px;font-size:12px;color:#516F90"><strong>Posología:</strong> ${aprobarPosologia}</div>
            <div style="margin-top:6px;font-size:12px;color:#516F90"><strong>Recetas:</strong> ${aprobarCantidad} · <strong>Emisor:</strong> ${emisorTratamientoFlag === 'administrativo' ? 'Administrativo' : 'Médico'}</div>
          `,
          showConfirmButton: false,
          timer: 4500,
          timerProgressBar: true,
          icon: webhookOk ? 'success' : 'warning',
          customClass: { popup: 'swal-toast-crm' },
          background: '#fff'
        });
      } catch (e) {
        console.error('Error aprobando pedido:', e);
        toast('Error al aprobar pedido: ' + (e.message || ''), 'error');
        setModalAprobarOpen(true);
        setAprobandoPedidoId(selectedPedidoId);
      } finally {
        setRechazoSubmitting(false);
      }
    })();
  }

  function abrirRechazar(id) {
    setSelectedPedidoId(id);
    setAprobandoPedidoId(null);
    setRechazoMotivo('');
    setModalRechazarOpen(true);
  }

  async function enviarMensajeInternoPedido(pedido) {
    const pedidoId = Number(pedido?.id);
    const mensaje = String(internalMessageDrafts[pedidoId] || '').trim();
    if (!pedidoId || !mensaje) {
      toast('Escribí un mensaje interno para enviar a administrativo.', 'error');
      return;
    }

    setInternalMessageSaving((prev) => ({ ...prev, [pedidoId]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${pedidoId}/mensaje-interno`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mensaje,
          actor: 'medico',
          author_label: pedido?.medico ? formatDoctorDisplayName(pedido.medico) : 'Médico'
        })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || `HTTP ${res.status}`);
      const updated = json.pedido || {};
      setData((current) => ({
        ...current,
        pedidos: current.pedidos.map((item) => (
          Number(item.id) === pedidoId ? { ...item, message_history: updated.message_history ?? item.message_history } : item
        ))
      }));
      setInternalMessageDrafts((prev) => ({ ...prev, [pedidoId]: '' }));
      toast('Mensaje interno enviado al área administrativa.', 'success');
    } catch (error) {
      console.error('Error enviando mensaje interno del pedido:', error);
      toast(`No se pudo enviar el mensaje interno: ${error.message || ''}`, 'error');
    } finally {
      setInternalMessageSaving((prev) => ({ ...prev, [pedidoId]: false }));
    }
  }

  async function solicitarConfirmacionRechazo() {
    const motivo = String(rechazoMotivo || '').trim();
    if (!motivo) {
      toast('Ingresá el motivo del rechazo', 'error');
      return;
    }
    const pedido = data.pedidos.find((p) => Number(p.id) === Number(selectedPedidoId));
    const pacienteLabel = pedido?.paciente || 'este pedido';
    const esExterno = Boolean(pedido?.es_externo);
    setModalRechazarOpen(false);
    setAprobandoPedidoId(null);
    const confirmResult = await fireConfirmDialog({
      title: 'Rechazar pedido',
      html: buildCrmSwalBody({
        lead: `Se rechazará el pedido de <strong>${pacienteLabel}</strong>.`,
        items: esExterno
          ? ['El paciente recibe aviso con el motivo indicado', 'Es un paciente de alta manual: se quitará del listado de altas pendientes']
          : ['El paciente recibe aviso con el motivo indicado']
      }),
      icon: 'warning',
      confirmButtonText: 'Rechazar',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#dc2626'
    });
    if (!confirmResult.isConfirmed) {
      setModalRechazarOpen(true);
      setAprobandoPedidoId(null);
      return;
    }
    await ejecutarRechazo(motivo);
  }

  async function ejecutarRechazo(motivo) {
      setRechazoSubmitting(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${selectedPedidoId}/rechazar`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ motivo })
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || `HTTP ${res.status}`);
        }
        const json = await res.json();
        const updated = json.pedido || json;
        const externoEliminado = json.externo_eliminado === true;
        setData((d) => ({
          ...d,
          pedidos: externoEliminado
            ? d.pedidos.filter(p => p.id !== selectedPedidoId)
            : d.pedidos.map(p => p.id === selectedPedidoId ? { ...p, estado: updated.estado || 'rechazado', motivo_rechazo: updated.motivo_rechazo || motivo } : p)
        }));
        setModalRechazarOpen(false);
        setRechazoMotivo('');
        // cerrar el panel de historial después de confirmar el rechazo
        setHistoryOpen(false);
        setSelectedPedidoId(null);
        setHistoryPage(1);
        Swal.fire({
          toast: true,
          position: 'bottom-end',
          html: `
            <div style="font-weight:700;color:#253342">Pedido rechazado</div>
            <div style="font-size:13px;color:#33475B;margin-top:6px">Se notificará al paciente.</div>
            ${externoEliminado ? '<div style="margin-top:6px;font-size:12px;color:#7c3aed;font-weight:700">✦ Paciente de alta manual dado de baja del listado</div>' : ''}
            <div style="margin-top:8px;font-size:12px;color:#516F90"><strong>Motivo:</strong> ${motivo}</div>
          `,
          showConfirmButton: false,
          timer: 4500,
          timerProgressBar: true,
          icon: 'error',
          customClass: { popup: 'swal-toast-crm' },
          background: '#fff'
        });
      } catch (e) {
        console.error('Error rechazando pedido:', e);
        toast('Error al rechazar pedido: ' + (e.message || ''), 'error');
      } finally {
        setRechazoSubmitting(false);
      }
  }

  function abrirEmitir(id) {
    const r = data.recetas.find(x => x.id === id);
    setSelectedRecetaId(id);
    setEmitirNro(`REC-EXT-${String(id).padStart(3, '0')}`);
    setModalEmitirOpen(true);
  }

  function confirmarEmision() {
    if (!emitirNro.trim()) { toast('Ingresá el número de receta', 'error'); return; }
    setData((d) => {
      const updated = { ...d };
      updated.recetas = d.recetas.map(r => r.id === selectedRecetaId ? { ...r, estado: 'emitida_sistema_externo', nro_externo: emitirNro, fecha_emitida: new Date().toLocaleDateString('es-AR') } : r);
      // persist a minimal emitted-receta payload so PedidosCallCenter can pick it up after navigation
      try {
        const receta = updated.recetas.find(r => r.id === selectedRecetaId);
        if (receta) {
          const emitPayload = {
            receta_id: receta.id,
            paciente: receta.paciente || '',
            medicamento: receta.medicamento || '',
            direccion: receta.direccion || '',
            estado: 'pendiente_call_center',
            nro_pedido: null,
            fecha: new Date().toISOString().split('T')[0]
          };
          sessionStorage.setItem('recentEmittedReceta', JSON.stringify(emitPayload));
        }
      } catch (e) { /* ignore storage errors */ }
      return updated;
    });
    setModalEmitirOpen(false);
    toast('Receta registrada como emitida.', 'success');
    try {
      const estado = 'emitida_sistema_externo';
      const estadoIsEmitida = (s) => typeof s === 'string' && /emit/i.test(s);
      if (estadoIsEmitida(estado)) {
        navigate('/contact-center');
      }
    } catch (e) {
      // noop if navigation fails in this environment
    }
  }

  function abrirFarmacia(id) {
    setSelectedFarmaciaId(id);
    setFarmaciaNro(`FAR-${new Date().getFullYear()}-${String(id).padStart(3, '0')}`);
    setModalFarmaciaOpen(true);
  }

  function confirmarPedidoFarmacia() {
    if (!farmaciaNro.trim()) { toast('Ingresá el número de pedido', 'error'); return; }
    setData((d) => ({ ...d, pedidos_farmacia: d.pedidos_farmacia.map(p => p.id === selectedFarmaciaId ? { ...p, estado: 'pedido_generado_farmacia', nro_pedido: farmaciaNro, notas: farmaciaNotas } : p) }));
    setModalFarmaciaOpen(false);
    toast(`Pedido ${farmaciaNro} generado en sistema de farmacia.`, 'success');
  }

  function abrirEstadoFarmacia(id) {
    setSelectedFarmaciaId(id);
    setModalEstadoOpen(true);
  }

  function confirmarEstadoFarmacia(nuevoEstado) {
    setData((d) => ({ ...d, pedidos_farmacia: d.pedidos_farmacia.map(p => p.id === selectedFarmaciaId ? { ...p, estado: nuevoEstado } : p) }));
    setModalEstadoOpen(false);
    toast('Estado actualizado correctamente.', 'info');
  }

  const styles = {
    container: { padding: isMobile ? '0.75rem' : '1.25rem', display: 'grid', gap: 10 },
    headerTitle: { fontSize: '1.25rem', fontWeight: 700, color: 'var(--hs-text)' },
    headerSub: { color: 'var(--hs-text-xlight)', marginTop: 6 },
    kpiGrid: { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : (isTablet ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,1fr)'), gap: 10, marginBottom: 8 },
    statNum: { fontFamily: 'monospace', fontSize: 24, fontWeight: 700, color: '#253342' },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { background: 'var(--hs-white)', borderRadius: 12, padding: isMobile ? 14 : 20, width: isMobile ? '94vw' : 520, maxWidth: '94vw', boxSizing: 'border-box', boxShadow: 'var(--sm)', color: 'var(--hs-text)' },
    formInput: { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hs-border)', fontSize: 14, boxSizing: 'border-box' },
    loadingBox: {
      border: '1px solid #DFE3EB',
      borderRadius: 12,
      padding: 28,
      textAlign: 'center',
      color: '#7C98B6',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      minHeight: 180,
      boxShadow: '0 1px 2px rgba(0,0,0,.05)'
    },
    aprobarDerivBox: {
      border: '1px dashed #c7d2fe',
      borderRadius: 10,
      padding: 12,
      background: '#f8fbff',
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    },
    aprobarDerivFieldLabel: {
      fontSize: 11,
      color: '#7C98B6',
      textTransform: 'uppercase',
      letterSpacing: '.06em',
      fontWeight: 700,
      marginBottom: 4
    },
    cardAvatar: {
      width: 38,
      height: 38,
      borderRadius: '50%',
      background: 'linear-gradient(145deg,#eff6ff,#dbeafe)',
      color: '#1d4ed8',
      border: '1px solid rgba(37,99,235,.28)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 800,
      flexShrink: 0
    },
    cardPacienteName: {
      fontSize: 14,
      fontWeight: 800,
      color: '#253342',
      lineHeight: 1.2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: isMobile ? 'normal' : 'nowrap'
    },
    cardMetaRow: {
      marginTop: 3,
      fontSize: 12,
      color: '#7C98B6',
      lineHeight: 1.35,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: isMobile ? 'normal' : 'nowrap'
    },
    cardMedicoBox: {
      width: isMobile ? '100%' : 240,
      minWidth: isMobile ? 0 : 200,
      display: 'flex',
      flexDirection: 'column',
      alignItems: isMobile ? 'flex-start' : 'flex-end',
      gap: 6,
      background: '#fff',
      border: '1px solid #DFE3EB',
      borderRadius: 10,
      padding: '6px 8px',
      boxSizing: 'border-box',
      justifySelf: isMobile ? 'stretch' : 'end'
    },
    aprobarDerivSearchInput: {
      padding: '8px 12px',
      borderRadius: 8,
      border: '1px solid #DFE3EB',
      fontSize: 13,
      background: '#fff',
      width: '100%',
      maxWidth: 'none',
      minWidth: 0,
      flex: 'none',
      boxSizing: 'border-box',
      marginBottom: 6
    },
    spinner: {
      width: 34,
      height: 34,
      borderRadius: '50%',
      border: '3px solid #DFE3EB',
      borderTopColor: '#2365AB',
      animation: 'pedidos-recetas-medico-spin .8s linear infinite'
    }
  };
  function handleAltaRapidaPedidoCreated(nuevoBase) {
    const nuevo = {
      ...nuevoBase,
      solicitado: nuevoBase.fecha_solicitud ? formatSolicitud(nuevoBase.fecha_solicitud) : '',
      recetas_generadas: resolveRecetasEmitidas(nuevoBase)
    };
    setData((d) => ({ ...d, pedidos: [nuevo, ...(Array.isArray(d.pedidos) ? d.pedidos : [])] }));
    setModalAltaRapidaOpen(false);
    setAprobandoPedidoId(null);
    markPedidoNuevo(nuevo.id);
    setOpenItems([nuevo.id]);
    setHistoryOpen(false);
    toast('Pedido creado. Completá la aprobación del tratamiento.', 'success');
    abrirAprobar(nuevo.id);
  }

  useEffect(() => {
    if (!editarMedicamentoPedidoId) return undefined;
    const ac = new AbortController();
    const timerId = setTimeout(async () => {
      setEditarMedicamentosLoading(true);
      try {
        const q = String(editarMedicamentoSearch || '').trim();
        const qs = new URLSearchParams();
        qs.set('limit', q ? '120' : '80');
        if (q) qs.set('q', q);
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/medicamentos?${qs.toString()}`, { credentials: 'include', signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json().catch(() => ({}));
        const meds = Array.isArray(json) ? json : (Array.isArray(json?.medicamentos) ? json.medicamentos : []);
        setEditarMedicamentos(meds);
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.error('Error cargando vademecum para editar medicamento:', e);
          toast('No se pudo cargar la lista de medicamentos', 'error');
          setEditarMedicamentos([]);
        }
      } finally {
        setEditarMedicamentosLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timerId);
      ac.abort();
    };
  }, [editarMedicamentoPedidoId, editarMedicamentoSearch]);

  const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
    <input
      ref={ref}
      value={value}
      onClick={onClick}
      placeholder={placeholder}
      readOnly
      style={{ ...styles.formInput, border: '1px solid var(--hs-border)', background: '#fff' }}
    />
  ));

  const workspaceHistoryClass = historyOpen && selectedPedido && !isMobile && !isMidDesktop && !isSmallLandscape
    ? ' crm-prm-workspace--with-history'
    : '';

  return (
    <section className="crm-gd-page crm-prm-page crm-pbody">
      <style>
        {`
          .crm-main > .crm-prm-page.crm-gd-page.crm-pbody {
            flex: 1 1 0 !important;
            min-height: 0 !important;
            overflow: hidden !important;
            display: flex !important;
            flex-direction: column !important;
            padding: 0 !important;
            gap: 0 !important;
          }
          .crm-prm-page .crm-prm-shell {
            display: flex !important;
            flex-direction: column !important;
            flex: 1 1 0 !important;
            min-height: 0 !important;
            overflow: hidden !important;
            padding: 16px !important;
            gap: 14px !important;
            box-sizing: border-box !important;
            background:
              radial-gradient(circle at 12% 0%, rgba(35, 101, 171, 0.07) 0%, transparent 34%),
              radial-gradient(circle at 88% 100%, rgba(116, 172, 223, 0.1) 0%, transparent 38%),
              linear-gradient(180deg, #f8fbff 0%, #f3f7fc 100%) !important;
          }
          .crm-prm-page .crm-prm-top {
            flex: 0 0 auto !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 14px !important;
          }
          .crm-prm-page .crm-prm-scroll {
            flex: 1 1 0 !important;
            min-height: 0 !important;
            overflow-x: hidden !important;
            overflow-y: auto !important;
            overscroll-behavior: contain !important;
            -webkit-overflow-scrolling: touch !important;
            padding-right: 2px !important;
            scrollbar-gutter: stable !important;
          }
          .crm-prm-page .crm-prm-main {
            min-height: 0 !important;
          }
        `}
      </style>

      <div className="crm-prm-shell">
      <div className="crm-prm-top">

      <div className="crm-gd-kpi-grid" style={isMobile ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : undefined}>
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num">{pendientesMedico.length}</div>
          <div className="crm-gd-kpi-card__label">Pendientes</div>
        </div>
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num">{data.pedidos.filter(p => p.estado === 'aprobado').length}</div>
          <div className="crm-gd-kpi-card__label">Aprobados hoy</div>
        </div>
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num">{data.pedidos.filter(p => p.estado === 'rechazado').length}</div>
          <div className="crm-gd-kpi-card__label">Rechazados</div>
        </div>
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num">{data.pedidos.filter(p => p.estado === 'en_tratamiento').length}</div>
          <div className="crm-gd-kpi-card__label">En tratamiento</div>
        </div>
      </div>

      </div>

      <div className="crm-prm-scroll">
      <div className="crm-prm-main">
        <div className="crm-gd-toolbar">
          <div className="crm-gd-toolbar__search">
            <CwListSearch
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar paciente, DNI o medicamento…"
            />
          </div>
          <span className="crm-prm-toolbar-meta">{pendientesResultadoLabel}</span>
          <div className="crm-gd-toolbar__actions">
            {canAltaRapida ? (
              <button
                type="button"
                className="crm-gd-btn crm-gd-btn--ghost"
                onClick={() => setModalAltaRapidaOpen(true)}
              >
                <span aria-hidden>＋</span>
                Alta rápida
              </button>
            ) : null}
            <button
              type="button"
              className="crm-gd-btn crm-gd-btn--ghost"
              onClick={openHistorialGlobal}
            >
              Historial global
            </button>
            <ColaMedicaFloatingAlert
              variant="inline"
              resumen={colaResumen}
              onVerPrimero={scrollToPrimerPedidoCola}
            />
          </div>
        </div>

        <div className={`crm-prm-workspace${workspaceHistoryClass}`}>
          <div className="crm-prm-list-col">
            {(() => {
              if (bootstrapListLoading) {
                return (
                  <div className="crm-gd-loading" role="status" aria-live="polite">
                    <div className="crm-gd-spinner" aria-hidden="true" />
                    <div className="crm-gd-loading__title">Preparando revisión de pedidos</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      Cargando pedidos pendientes…
                    </div>
                  </div>
                );
              }
              if (pendientesMedico.length === 0) {
                return (
                  <div className="crm-gd-empty crm-prm-empty-card">
                    No hay pedidos pendientes de revisión
                  </div>
                );
              }
              if (filteredPendientes.length === 0) {
                return (
                  <div className="crm-gd-empty crm-prm-empty-card">
                    No se encontraron coincidencias para &quot;<strong>{search || ''}</strong>&quot;
                  </div>
                );
              }
              return (
                <div className="crm-gd-list crm-prm-pedidos-list">
                  {pendientesAgrupadosPorPaciente.flatMap((group) => {
                const isMulti = group.pedidos.length > 1;
                const primary = group.primary;
                const groupHistorialActivo = historyOpen && group.pedidos.some((x) => Number(x.id) === Number(selectedPedidoId));

                const pedidoCards = group.pedidos.map((p) => {
                const colaVisual = p.estado === 'pendiente_aprobacion_medica'
                  ? getColaMedicaCardVisual(p, colaTick)
                  : null;
                const theme = colaVisual
                  ? { accent: colaVisual.accent, soft: colaVisual.soft, badgeBg: colaVisual.soft, badgeColor: colaVisual.text, badgeBorder: colaVisual.border }
                  : p.estado === 'aprobado'
                    ? { accent: '#00BDA5', soft: 'rgba(0,189,165,.12)', badgeBg: 'linear-gradient(145deg,#ecfeff,#ccfbf1)', badgeColor: '#0f766e', badgeBorder: 'rgba(20,184,166,.28)' }
                    : { accent: '#2365AB', soft: 'rgba(35,101,171,.12)', badgeBg: 'linear-gradient(145deg,#eff6ff,#dbeafe)', badgeColor: '#1d4ed8', badgeBorder: 'rgba(37,99,235,.28)' };
                const colaCardClass = colaVisual ? ` crm-pedido-cola-card crm-pedido-cola-card--${colaVisual.tier}` : '';
                const medicationDisplay = buildMedicationDisplay(p);
                const historialActivo = !isMulti && historyOpen && Number(selectedPedidoId) === Number(p.id);
                const hidePatientHeader = isMulti;
                const esNuevo = isPedidoNuevo(p.id);
                const nuevoRing = '0 0 0 2px rgba(34,197,94,.38)';
                const cardShellShadow = colaVisual
                  ? (esNuevo ? `${nuevoRing}, ${colaVisual.cardBoxShadow}` : colaVisual.cardBoxShadow)
                  : (esNuevo ? `${nuevoRing}, 0 1px 2px rgba(0,0,0,.05)` : '0 1px 2px rgba(0,0,0,.05)');
                return (
                  <div
                    className={`${hidePatientHeader ? 'crm-pedido-receta-card' : 'crm-icard crm-pedido-receta-card'}${colaCardClass}`}
                    key={p.id}
                    id={`card-pedido-${p.id}`}
                    style={hidePatientHeader
                      ? {
                        borderTop: colaVisual ? `1px solid ${colaVisual.border}` : '1px solid rgba(148,163,184,.2)',
                        background: '#fff',
                        boxShadow: cardShellShadow
                      }
                      : {
                        marginBottom: 12,
                        border: colaVisual?.cardBorder || '1px solid #DFE3EB',
                        borderRadius: 12,
                        overflow: 'hidden',
                        boxShadow: cardShellShadow
                      }}
                  >
                    <div
                      className="crm-icard-h"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr auto' : ((isTablet || isSmallLandscape) ? '1fr auto' : '1fr auto auto'),
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: 10,
                        padding: '8px 10px',
                        background: colaVisual?.headerBg || '#fff',
                        boxShadow: colaVisual?.insetShadow || (hidePatientHeader ? 'inset 3px 0 0 rgba(242,84,91,.35)' : `inset 3px 0 0 ${theme.accent}`),
                        overflow: 'visible'
                      }}
                    >
                      {hidePatientHeader ? (
                        <div style={{ flex: 1, minWidth: 0, gridColumn: isMobile ? '1 / -1' : undefined }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: '#253342', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={medicationDisplay.title}>
                              {medicationDisplay.title}
                            </div>
                            <PedidoNuevoBadge show={esNuevo} />
                          </div>
                          {medicationDisplay.genericLine ? (
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={medicationDisplay.genericLine}>
                              {medicationDisplay.genericLine}
                            </div>
                          ) : null}
                          {p.estado === 'pendiente_aprobacion_medica' ? (
                            <div style={{ marginTop: 4 }}>
                              <ColaMedicaBadge pedido={p} nowMs={colaTick} variant="inline" compact />
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedPedidoId(p.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedPedidoId(p.id);
                            }
                          }}
                          style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0, cursor: 'pointer' }}
                        >
                          <div className="crm-gd-avatar">{cardPatientInitials(p.paciente)}</div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
                              <div className="crm-gd-card__name" style={{ flex: '1 1 auto', minWidth: 0 }}>{p.paciente}</div>
                              <PedidoNuevoBadge show={esNuevo} />
                              <PacienteExternoBadge show={p.es_externo} compact />
                              {p.estado === 'pendiente_aprobacion_medica' ? (
                                <span style={{ flexShrink: 0 }}>
                                  <ColaMedicaBadge pedido={p} nowMs={colaTick} variant="inline" compact />
                                </span>
                              ) : null}
                            </div>
                            <div className="crm-gd-card__meta">
                              DNI {formatDniDisplay(p.dni)}{p.obra_social ? ` · ${p.obra_social}` : ''}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="crm-prm-card-medico-box" style={isMobile ? { gridColumn: '1 / -1' } : undefined}>
                        {p.medico ? (
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: '#253342',
                              textAlign: isMobile ? 'left' : 'right',
                              width: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={formatMedicoTitulo(p.medico)}
                          >
                            {formatMedicoTitulo(p.medico)}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Sin médico asignado</div>
                        )}
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end', width: '100%' }}>
                          {estadoPillElem(p, { compact: true, nowMs: colaTick })}
                          {!hidePatientHeader && p.estado === 'pendiente_aprobacion_medica' ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (historialActivo) {
                                  setHistoryOpen(false);
                                  setSelectedPedidoId(null);
                                } else {
                                  setSelectedPedidoId(p.id);
                                  setHistoryOpen(true);
                                  setHistoryPage(1);
                                }
                              }}
                              aria-label={historialActivo ? 'Ocultar historial' : 'Ver historial'}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                borderRadius: 999,
                                fontSize: 10,
                                fontWeight: 700,
                                border: historialActivo ? '1px solid rgba(35,101,171,.45)' : '1px solid rgba(148,163,184,.35)',
                                background: historialActivo ? '#eff6ff' : '#fff',
                                color: historialActivo ? '#1d4ed8' : '#475569',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'background .18s ease, border-color .18s ease'
                              }}
                            >
                              Historial
                            </button>
                          ) : null}
                        </div>
                      </div>

                      <button
                        type="button"
                        aria-label={openItems.some((x) => Number(x) === Number(p.id)) ? 'Cerrar' : 'Abrir'}
                        onClick={() => togglePedidoCard(p.id)}
                        style={{
                          border: '1px solid rgba(148,163,184,.28)',
                          background: '#fff',
                          cursor: 'pointer',
                          padding: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 999,
                          transition: 'background .18s ease, transform .28s cubic-bezier(.2,.9,.2,1)',
                          boxShadow: '0 2px 8px rgba(15,23,42,.08)',
                          justifySelf: isMobile ? 'end' : 'center',
                          alignSelf: isMobile ? 'start' : 'center'
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{
                            display: 'block',
                            color: 'var(--hs-text)',
                            transform: openItems.some((x) => Number(x) === Number(p.id)) ? 'rotate(90deg) translateX(1px)' : 'rotate(0deg)',
                            transition: 'transform .32s cubic-bezier(.2,.9,.2,1), opacity .18s ease',
                            opacity: 0.95
                          }}
                          aria-hidden
                        >
                          <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="currentColor" />
                        </svg>
                      </button>
                    </div>
                    <div
                      ref={el => contentRefs.current[p.id] = el}
                      style={{ overflow: 'hidden', transition: 'max-height .38s cubic-bezier(.2,.9,.2,1), opacity .28s ease', maxHeight: openItems.some((x) => Number(x) === Number(p.id)) ? (contentRefs.current[p.id]?.scrollHeight || 1300) + 'px' : '0px', opacity: openItems.some((x) => Number(x) === Number(p.id)) ? 1 : 0 }}
                    >
                      <div className="crm-icard-b" style={{ background: '#fff', borderTop: '1px solid rgba(148,163,184,.18)', padding: '14px 14px 18px', boxSizing: 'border-box' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: (isMobile || isMidDesktop) ? '1fr' : '1.8fr 0.9fr', gap: 10, marginBottom: 14 }}>
                          <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.18)', borderRadius: 12, padding: '10px 12px' }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Medicamento</div>
                            {editarMedicamentoPedidoId === p.id ? (
                              <div style={{ display: 'grid', gap: 8 }}>
                                <input
                                  type="search"
                                  value={editarMedicamentoSearch}
                                  onChange={(e) => {
                                    setEditarMedicamentoSearch(e.target.value);
                                    setEditarMedicamentoPickerOpen(true);
                                  }}
                                  placeholder="Buscar por nombre, laboratorio o id…"
                                  disabled={editarMedicamentoSaving || editarMedicamentosLoading}
                                  style={{ ...styles.formInput, minHeight: 36 }}
                                />
                                <div ref={editarMedicamentoPickerRef} style={{ position: 'relative' }}>
                                  <div
                                    style={{
                                      width: '96%',
                                      minHeight: 34,
                                      padding: '6px 10px',
                                      borderRadius: 8,
                                      border: '1px solid #cbd5e1',
                                      fontSize: 12,
                                      background: '#fff',
                                      color: '#334155'
                                    }}
                                  >
                                    {editarMedicamentosLoading
                                      ? 'Buscando medicamento…'
                                      : (editarMedicamentoChosen ? `Seleccionado: ${formatMedicamentoOptionLabel(editarMedicamentoChosen)}` : 'Seleccioná un medicamento de la lista')}
                                  </div>
                                  {(editarMedicamentoPickerOpen || String(editarMedicamentoSearch || '').trim()) && !editarMedicamentosLoading ? (
                                    <div
                                      role="listbox"
                                      style={{
                                        position: 'relative',
                                        top: 'auto',
                                        left: 'auto',
                                        right: 'auto',
                                        marginTop: 4,
                                        maxHeight: 270,
                                        overflowY: 'auto',
                                        zIndex: 9999,
                                        background: '#fff',
                                        border: '1px solid rgba(148,163,184,.32)',
                                        borderRadius: 10,
                                        boxShadow: '0 14px 36px rgba(15,23,42,.14), 0 2px 8px rgba(15,23,42,.06)'
                                      }}
                                    >
                                      {editarMedicamentos.map((m) => {
                                        const selected = Number(editarMedicamentoChosen?.id) === Number(m.id);
                                        return (
                                          <button
                                            key={`edit-med-${p.id}-${m.id}`}
                                            type="button"
                                            onClick={() => {
                                              setEditarMedicamentoChosen(m);
                                              setEditarMedicamentoPickerOpen(false);
                                              setEditarMedicamentoSearch('');
                                            }}
                                            style={{
                                              width: '100%',
                                              padding: '9px 10px',
                                              border: 'none',
                                              borderBottom: '1px solid #f1f5f9',
                                              background: selected ? '#eff6ff' : '#fff',
                                              cursor: 'pointer',
                                              fontSize: 12,
                                              color: '#253342',
                                              textAlign: 'left'
                                            }}
                                          >
                                            {formatMedicamentoOptionLabel(m)}
                                          </button>
                                        );
                                      })}
                                      {editarMedicamentos.length === 0 ? (
                                        <div style={{ padding: '10px 12px', fontSize: 12, color: '#64748b' }}>
                                          Sin resultados para la búsqueda.
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                  <button
                                    type="button"
                                    onClick={cancelarEditarMedicamento}
                                    disabled={editarMedicamentoSaving}
                                    style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.35)', background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={guardarEditarMedicamento}
                                    disabled={editarMedicamentoSaving || !Number.isFinite(Number(editarMedicamentoChosen?.id))}
                                    style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #0f766e', background: '#0f766e', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 12, opacity: (editarMedicamentoSaving || !Number.isFinite(Number(editarMedicamentoChosen?.id))) ? 0.7 : 1 }}
                                  >
                                    {editarMedicamentoSaving ? 'Guardando…' : 'Guardar medicamento'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => abrirEditarMedicamento(p)}
                                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}
                                title="Hacé click para editar el medicamento"
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    border: '1px solid rgba(148,163,184,.18)',
                                    borderRadius: 12,
                                    background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
                                    padding: '10px 12px'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                                    <div style={{ fontSize: 15, fontWeight: 800, color: '#253342', overflowWrap: 'anywhere', flex: 1, minWidth: 0 }}>
                                      {medicationDisplay.title}
                                    </div>
                                    {medicationDisplay.laboratoryLabel ? (
                                      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textAlign: 'right', maxWidth: '45%', flexShrink: 0 }}>
                                        {medicationDisplay.laboratoryLabel}
                                      </div>
                                    ) : null}
                                  </div>
                                  {medicationDisplay.genericLine ? (
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#475569', overflowWrap: 'anywhere' }}>
                                      {medicationDisplay.genericLine}
                                    </div>
                                  ) : null}
                                  {medicationDisplay.meta.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                                      {medicationDisplay.meta.map((item) => (
                                        <span
                                          key={`${p.id}-${item}`}
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: '4px 8px',
                                            borderRadius: 999,
                                            border: '1px solid rgba(37,99,235,.16)',
                                            background: '#eff6ff',
                                            color: '#1e3a8a',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            lineHeight: 1.2
                                          }}
                                        >
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null}
                                  {medicationDisplay.detailLines.length > 0 ? (
                                    <div style={{ display: 'grid', gap: 2, marginTop: 8 }}>
                                      {medicationDisplay.detailLines.map((item) => (
                                        <div key={`${p.id}-detail-${item}`} style={{ fontSize: 12, color: '#475569', overflowWrap: 'anywhere' }}>
                                          {item}
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                  {p.acepta_recordatorios ? (
                                    <div
                                      style={{
                                        marginTop: 8,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        padding: '5px 9px',
                                        borderRadius: 999,
                                        background: '#F0F7F5',
                                        color: '#007A63',
                                        fontWeight: 700,
                                        fontSize: 11,
                                        lineHeight: 1.2
                                      }}
                                    >
                                      Suscripto a recordatorios
                                    </div>
                                  ) : null}
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); abrirEditarMedicamento(p); }}
                                  style={{ width: 28, height: 28, borderRadius: 999, border: '1px solid #d2e2f4', background: '#f8fbff', color: '#1c4f86', cursor: 'pointer', fontWeight: 800, fontSize: 14, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                                  title="Editar medicamento de este pedido"
                                  aria-label="Editar medicamento"
                                >
                                  ✎
                                </button>
                              </div>
                            )}
                          </div>
                          <div style={{ background: '#fff', border: '1px solid rgba(148,163,184,.18)', borderRadius: 12, padding: '10px 12px' }}>
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>Detalle de solicitud</div>
                            <div style={{ marginBottom: 8, color: 'var(--hs-text)', fontSize: 13 }}><strong>Solicitado:</strong> {p.solicitado || '—'}</div>
                            {isFamilyRequest(p) ? (
                              <div style={{ marginBottom: 8, color: '#35506d', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <strong>Solicitado por:</strong> Familiar (DNI {formatDniDisplay(p.solicitante_dni)})
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const titularDni = String(p.solicitante_dni || '').replace(/\D/g, '').replace(/^0+/, '');
                                    navigate(`/asociacion-clientes?dni=${encodeURIComponent(titularDni)}&tab=vinculos`);
                                  }}
                                  style={{
                                    border: '1px solid #d2e2f4',
                                    background: '#f8fbff',
                                    color: '#1c4f86',
                                    borderRadius: 999,
                                    padding: '4px 10px',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                  title="Ver titular en Asociación clientes"
                                >
                                  Ver titular
                                </button>
                              </div>
                            ) : null}
                            {(() => {
                              const internalMessages = parsePedidoInternalMessages(p.message_history);
                              const lastInternalMessage = internalMessages[internalMessages.length - 1] || null;
                              return (
                                <div style={{ marginTop: 10, borderRadius: 16, border: '1px solid rgba(90,127,191,.16)', background: 'linear-gradient(135deg, rgba(239,246,255,.95), rgba(255,255,255,.98))', padding: '12px 13px', boxShadow: '0 14px 28px rgba(37,99,235,.08)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em' }}>Canal interno</div>
                                      <div style={{ marginTop: 2, color: '#16365f', fontSize: 13, fontWeight: 800 }}>Chat rápido con administración</div>
                                      <div style={{ marginTop: 5, fontSize: 11.5, color: '#516f90', lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: window.innerWidth <= 980 ? '100%' : 260 }}>
                                        {lastInternalMessage
                                          ? `${resolvePedidoInternalDoctorLabel(lastInternalMessage, p)}${lastInternalMessage.timestamp ? ` · ${lastInternalMessage.timestamp}` : ''} · ${lastInternalMessage.text}`
                                          : 'Sin mensajes todavía. Abre el chat para enviar una consulta breve.'}
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '5px 10px', borderRadius: 999, background: '#e9f2ff', color: '#1d4f91', fontSize: 10.5, fontWeight: 800, boxShadow: 'inset 0 0 0 1px rgba(59,130,246,.12)' }}>
                                        {internalMessages.length > 0 ? `${internalMessages.length} mensaje${internalMessages.length === 1 ? '' : 's'}` : 'Sin mensajes'}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInternalChatPedidoId(p.id);
                                        }}
                                        style={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 8,
                                          padding: '9px 14px',
                                          borderRadius: 999,
                                          border: '1px solid rgba(29,79,145,.18)',
                                          background: 'linear-gradient(135deg,#1d4f91,#2e6fd0)',
                                          color: '#fff',
                                          cursor: 'pointer',
                                          fontWeight: 800,
                                          boxShadow: '0 12px 22px rgba(29,79,145,.22)'
                                        }}
                                      >
                                        <span aria-hidden="true">◉</span>
                                        Abrir chat
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            gap: 10,
                            flexWrap: 'wrap',
                            marginTop: 4,
                            paddingTop: 14,
                            paddingBottom: 4,
                            borderTop: '1px solid rgba(148,163,184,.14)'
                          }}
                        >
                          <button type="button" className="crm-prm-btn-action crm-prm-btn-action--approve" onClick={(e) => { e.stopPropagation(); abrirAprobar(p.id); }}>✓ Aprobar tratamiento</button>
                          <button type="button" className="crm-prm-btn-action crm-prm-btn-action--reject" onClick={(e) => { e.stopPropagation(); abrirRechazar(p.id); }}>✕ Rechazar</button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
                });

                if (!isMulti) return pedidoCards;

                const groupTieneNuevo = group.pedidos.some((x) => isPedidoNuevo(x.id));
                const groupExpanded = isPatientGroupExpanded(group.dniKey);
                const groupCola = getGroupColaCardVisual(group.pedidos, colaTick);

                return [(
                  <div
                    key={`grp-${group.dniKey}`}
                    className={`crm-icard crm-pedido-receta-card crm-pedido-cola-card crm-pedido-cola-card--${groupCola.tier}`}
                    style={{
                      marginBottom: 12,
                      border: groupCola.cardBorder,
                      borderRadius: 12,
                      overflow: 'hidden',
                      boxShadow: groupTieneNuevo
                        ? `0 0 0 2px rgba(34,197,94,.38), ${groupCola.cardBoxShadow}`
                        : groupCola.cardBoxShadow
                    }}
                  >
                    <div
                      className="crm-icard-h crm-pedido-grupo-header"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : '1fr auto',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 10px',
                        background: groupCola.headerBg,
                        boxShadow: groupCola.insetShadow,
                        borderBottom: groupExpanded ? '1px solid rgba(148,163,184,.18)' : 'none'
                      }}
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedPedidoId(primary.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedPedidoId(primary.id);
                          }
                        }}
                        style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0, cursor: 'pointer' }}
                      >
                        <div className="crm-gd-avatar">{cardPatientInitials(group.paciente)}</div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="crm-gd-card__name" style={{ minWidth: 0 }}>{group.paciente}</div>
                          <div className="crm-gd-card__meta">
                            DNI {formatDniDisplay(group.dni)}{group.obra_social ? ` · ${group.obra_social}` : ''}
                          </div>
                        </div>
                      </div>
                      <div className="crm-pedido-grupo-header__actions">
                        <span className="crm-pedido-grupo-count">
                          {group.pedidos.length} pedido{group.pedidos.length === 1 ? '' : 's'}
                        </span>
                        <PedidoNuevoBadge show={groupTieneNuevo} />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (groupHistorialActivo) {
                              setHistoryOpen(false);
                              setSelectedPedidoId(null);
                            } else {
                              setSelectedPedidoId(primary.id);
                              setHistoryOpen(true);
                              setHistoryPage(1);
                            }
                          }}
                          aria-label={groupHistorialActivo ? 'Ocultar historial' : 'Ver historial'}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 700,
                            border: groupHistorialActivo ? '1px solid rgba(35,101,171,.45)' : '1px solid rgba(148,163,184,.35)',
                            background: groupHistorialActivo ? '#eff6ff' : '#fff',
                            color: groupHistorialActivo ? '#1d4ed8' : '#475569',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Historial
                        </button>
                        <button
                          type="button"
                          className={`crm-pedido-grupo-toggle${groupExpanded ? ' is-open' : ''}`}
                          aria-label={groupExpanded ? 'Ocultar pedidos del paciente' : `Ver ${group.pedidos.length} pedidos`}
                          aria-expanded={groupExpanded}
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePatientGroup(group.dniKey);
                          }}
                        >
                          <span className="crm-pedido-grupo-toggle__label">
                            {groupExpanded ? 'Ocultar' : 'Ver pedidos'}
                          </span>
                          <span className="crm-pedido-grupo-toggle__chev" aria-hidden>
                            {groupExpanded ? '▴' : '▾'}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className={`crm-pedido-grupo-body${groupExpanded ? ' is-open' : ''}`}>
                      <div className="crm-pedido-grupo-body__inner">
                        {pedidoCards}
                      </div>
                    </div>
                  </div>
                )];
              })}
                </div>
              );
            })()}
          </div>

          {historyOpen && selectedPedido ? (
          <aside className="crm-prm-history-rail">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div
                    className="crm-prm-history-rail__patient"
                    title={`${selectedPedido.paciente} — DNI ${formatDniDisplay(selectedPedido.dni)}`}
                  >
                    <div className="crm-prm-history-rail__patient-name" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {selectedPedido.paciente}
                      <PacienteExternoBadge show={selectedPedido.es_externo} compact />
                    </div>
                    <div className="crm-prm-history-rail__patient-meta">
                      DNI {formatDniDisplay(selectedPedido.dni)} · {selectedPedido.obra_social || '—'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'nowrap' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#334155', letterSpacing: '.02em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Historial
                      <span style={{ marginLeft: 4, color: '#475569', background: 'rgba(148,163,184,.16)', padding: '1px 6px', borderRadius: 999, fontWeight: 800 }}>
                        {filteredHistory.length}
                      </span>
                    </div>
                    <input
                      placeholder="Buscar…"
                      value={historySearch}
                      onChange={(e) => { setHistorySearch(e.target.value); setHistoryPage(1); }}
                      style={{ ...styles.formInput, flex: 1, minWidth: 0, padding: '4px 7px', fontSize: 11, borderRadius: 7, border: '1px solid rgba(148,163,184,.28)', background: '#fff' }}
                    />
                    <select
                      value={historyItemsPerPage}
                      onChange={(e) => { setHistoryItemsPerPage(Number(e.target.value)); setHistoryPage(1); }}
                      title="Ítems por página"
                      style={{ padding: '3px 5px', borderRadius: 7, border: '1px solid rgba(148,163,184,.28)', background: 'white', cursor: 'pointer', fontSize: 10, fontWeight: 700, width: 42 }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                    </select>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    paddingRight: 2
                  }}
                >
                  {pageHistoryItems.length === 0 ? (
                    <div style={{ padding: '8px 4px', fontSize: 11, color: 'var(--hs-text-xlight)', textAlign: 'center' }}>Sin coincidencias.</div>
                  ) : (
                    pageHistoryItems.map((h) => {
                      const clean = (v) => {
                        const s = String(v ?? '').trim();
                        if (!s) return '';
                        const low = s.toLowerCase();
                        return (low === '0' || low === 'null' || low === 'undefined') ? '' : s;
                      };
                      const marca = clean(h.nombre_comercial);
                      const lab = clean(h.laboratorio);
                      const comercialLine = [marca, lab].filter(Boolean).join(' · ');
                      const extraNote = h.estado === 'rechazado'
                        ? (h.motivo_rechazo || h.notas)
                        : h.notas;
                      const clampStyle = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' };
                      return (
                        <div
                          key={h.id}
                          className="crm-prm-history-item"
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 11, color: '#0f172a', lineHeight: 1.2 }}>{h.medicamento}</div>
                              {comercialLine ? (
                                <div style={{ fontSize: 9, color: '#475569', marginTop: 1, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={comercialLine}>
                                  {comercialLine}
                                </div>
                              ) : null}
                            </div>
                            <div style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                              <span style={{ transform: 'scale(.88)', transformOrigin: 'right top', display: 'inline-block' }}>{estadoPillElem(h, { nowMs: colaTick })}</span>
                              {h.fecha_solicitud ? <div style={{ fontSize: 9, color: '#64748b', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatSolicitud(h.fecha_solicitud)}</div> : null}
                            </div>
                          </div>

                          {h.posologia ? (
                            <div style={{ marginTop: 3, fontSize: 10, color: '#475569', background: 'rgba(37,99,235,.06)', padding: '3px 5px', borderRadius: 5, lineHeight: 1.3 }} title={h.posologia}>
                              <span style={{ fontWeight: 800, color: '#334155' }}>Posol. </span>
                              <span style={clampStyle}>{h.posologia}</span>
                            </div>
                          ) : null}

                          <div style={{ marginTop: 3, display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 9, color: '#33475B', fontWeight: 700, lineHeight: 1.25, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={String(h.medico || '')}>
                              {h.medico}{h.periodo ? ` · ${h.periodo}` : ''}
                            </div>
                            <div style={{ fontSize: 9, color: '#475569', background: 'rgba(148,163,184,.12)', padding: '1px 5px', borderRadius: 999, fontWeight: 800, flexShrink: 0 }}>
                              {h.recetas_generadas || 0}/{h.cantidad_recetas || 0}
                            </div>
                          </div>

                          {extraNote ? (
                            <div style={{ marginTop: 3, fontSize: 10, color: '#516F90', lineHeight: 1.3 }} title={extraNote}>
                              <span style={{ fontWeight: 800 }}>{h.estado === 'rechazado' ? 'Motivo: ' : 'Notas: '}</span>
                              <span style={clampStyle}>{extraNote}</span>
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 6,
                    flexShrink: 0,
                    paddingTop: 4,
                    borderTop: '1px solid rgba(148,163,184,.14)',
                    background: 'linear-gradient(180deg, rgba(248,250,252,.6), #f8fafc)'
                  }}
                >
                  <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <button
                      type="button"
                      disabled={historyPage <= 1}
                      onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                      style={{ padding: '3px 7px', borderRadius: 7, border: '1px solid rgba(148,163,184,.28)', background: historyPage <= 1 ? '#f1f5f9' : '#fff', cursor: historyPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: 10 }}
                    >
                      ◀
                    </button>
                    <div style={{ fontSize: 9, color: '#475569', fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {historyPage}/{totalHistoryPages}
                    </div>
                    <button
                      type="button"
                      disabled={historyPage >= totalHistoryPages}
                      onClick={() => setHistoryPage(p => Math.min(totalHistoryPages, p + 1))}
                      style={{ padding: '3px 7px', borderRadius: 7, border: '1px solid rgba(148,163,184,.28)', background: historyPage >= totalHistoryPages ? '#f1f5f9' : '#fff', cursor: historyPage >= totalHistoryPages ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: 10 }}
                    >
                      ▶
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => { setHistoryOpen(false); setSelectedPedidoId(null); }}
                    style={{ padding: '4px 8px', borderRadius: 7, border: '1px solid rgba(148,163,184,.3)', background: '#fff', cursor: 'pointer', fontWeight: 800, fontSize: 10, color: '#334155' }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
          </aside>
          ) : null}
        </div>
      </div>
      </div>
      </div>


      {internalChatPedido ? (() => {
        const popupMessages = parsePedidoInternalMessages(internalChatPedido.message_history);
        const isSendingInternalMessage = Boolean(internalMessageSaving[internalChatPedido.id]);
        return (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 260,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? 12 : 24,
              background: 'rgba(15,23,42,.44)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: isMobile ? '100%' : 520,
                maxWidth: '100%',
                maxHeight: 'min(82vh, 760px)',
                overflow: 'hidden',
                borderRadius: 24,
                border: '1px solid rgba(191,219,254,.42)',
                background: 'linear-gradient(180deg, #eef6ff 0%, #ffffff 24%, #ffffff 100%)',
                boxShadow: '0 34px 90px rgba(15,23,42,.30), 0 1px 0 rgba(255,255,255,.6) inset',
                display: 'grid',
                gridTemplateRows: 'auto 1fr auto'
              }}
            >
              <div style={{ padding: isMobile ? '16px 16px 14px' : '18px 20px 16px', borderBottom: '1px solid rgba(148,163,184,.16)', background: 'linear-gradient(135deg, rgba(219,234,254,.85), rgba(255,255,255,.72))' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 999, background: 'rgba(29,79,145,.08)', color: '#1d4f91', fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                      <span aria-hidden="true">◉</span>
                      Canal interno
                    </div>
                    <div style={{ marginTop: 10, fontSize: isMobile ? 18 : 20, fontWeight: 900, color: '#102a43', letterSpacing: '-.02em' }}>Chat rápido con administración</div>
                    <div style={{ marginTop: 6, fontSize: 12.5, color: '#486581', lineHeight: 1.45 }}>
                      {internalChatPedido.paciente} · DNI {formatDniDisplay(internalChatPedido.dni)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInternalChatPedidoId(null)}
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 999,
                      border: '1px solid rgba(148,163,184,.22)',
                      background: 'rgba(255,255,255,.84)',
                      color: '#334155',
                      cursor: 'pointer',
                      fontSize: 18,
                      fontWeight: 700,
                      flexShrink: 0
                    }}
                    aria-label="Cerrar chat interno"
                    title="Cerrar"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div style={{ padding: isMobile ? '14px 12px' : '16px 18px', overflowY: 'auto', background: 'linear-gradient(180deg, rgba(255,255,255,.55), rgba(248,250,252,.92))' }}>
                {popupMessages.length > 0 ? (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {popupMessages.map((msg) => {
                      const isMedical = msg.tone === 'medico';
                      const isLegacy = msg.tone === 'legacy';
                      const displayAuthor = resolvePedidoInternalDoctorLabel(msg, internalChatPedido);
                      return (
                        <div
                          key={msg.id}
                          style={{
                            marginLeft: isMedical ? (isMobile ? 20 : 80) : 0,
                            marginRight: isMedical ? 0 : (isMobile ? 20 : 80),
                            padding: '12px 14px',
                            borderRadius: 18,
                            border: isMedical ? '1px solid rgba(37,99,235,.16)' : '1px solid rgba(148,163,184,.18)',
                            background: isLegacy
                              ? 'linear-gradient(180deg,#fff7ed,#fffbeb)'
                              : (isMedical ? 'linear-gradient(180deg,#eaf3ff,#f8fbff)' : 'linear-gradient(180deg,#ffffff,#f8fafc)'),
                            boxShadow: '0 14px 24px rgba(15,23,42,.08)'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                            <strong style={{ fontSize: 12.5, color: isMedical ? '#1d4f91' : '#52606d', fontWeight: 900 }}>{displayAuthor}</strong>
                            {msg.timestamp ? <span style={{ fontSize: 11, color: '#7b8794', whiteSpace: 'nowrap' }}>{msg.timestamp}</span> : null}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', color: '#243b53', fontSize: 13, lineHeight: 1.6 }}>{msg.text}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: isMobile ? '20px 16px' : '26px 22px', borderRadius: 20, border: '1px dashed rgba(148,163,184,.28)', background: 'rgba(255,255,255,.82)', color: '#52606d', textAlign: 'center', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.7)' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#16365f' }}>Sin mensajes por ahora</div>
                    <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5 }}>
                      Usa este canal para una consulta puntual con administración, sin cargar altura extra en la card principal.
                    </div>
                  </div>
                )}
              </div>

              <div style={{ padding: isMobile ? '14px 12px 12px' : '16px 18px 18px', borderTop: '1px solid rgba(148,163,184,.16)', background: 'rgba(255,255,255,.92)' }}>
                <div style={{ display: 'grid', gap: 10 }}>
                  <textarea
                    value={internalMessageDrafts[internalChatPedido.id] || ''}
                    onChange={(e) => setInternalMessageDrafts((prev) => ({ ...prev, [internalChatPedido.id]: e.target.value }))}
                    placeholder="Escribe tu mensaje interno..."
                    style={{ ...styles.textarea, width: isMobile ? '100%' : '92%', marginLeft: isMobile ? 0 : 'auto', marginRight: isMobile ? 0 : 'auto', minHeight: isMobile ? 76 : 84, maxHeight: 140, resize: 'vertical', background: '#fff', marginBottom: 0, borderRadius: 16, border: '1px solid rgba(148,163,184,.2)', boxShadow: 'inset 0 1px 2px rgba(15,23,42,.04)' }}
                    disabled={isSendingInternalMessage}
                  />
                  <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => enviarMensajeInternoPedido(internalChatPedido)}
                      disabled={isSendingInternalMessage}
                      style={{
                        width: isMobile ? '100%' : 'auto',
                        minWidth: isMobile ? '100%' : 132,
                        padding: '12px 16px',
                        borderRadius: 999,
                        border: '1px solid rgba(29,79,145,.16)',
                        background: isSendingInternalMessage ? 'linear-gradient(135deg,#dbeafe,#eaf3ff)' : 'linear-gradient(135deg,#1d4f91,#2b6ccb)',
                        color: isSendingInternalMessage ? '#31557f' : '#fff',
                        cursor: isSendingInternalMessage ? 'not-allowed' : 'pointer',
                        fontWeight: 900,
                        boxShadow: isSendingInternalMessage ? 'none' : '0 16px 28px rgba(29,79,145,.22)'
                      }}
                    >
                      {isSendingInternalMessage ? 'Enviando...' : 'Enviar'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })() : null}

      <TratamientoAprobacionWizard
        key={`aprobar-${selectedPedidoId}-${aprobarWizardEpoch}`}
        open={modalAprobarOpen}
        onClose={() => setModalAprobarOpen(false)}
        pedido={data.pedidos.find((p) => p.id === selectedPedidoId) || null}
        isMobile={isMobile}
        isAdmin={isAdmin}
        linkedMedicoId={linkedMedicoId}
        linkedMedicoNombre={linkedMedicoNombre}
        medicosDerivacion={medicosDerivacion}
        medicosLoading={medicosLoading}
        submitting={rechazoSubmitting}
        meses={aprobarMeses}
        onMesesChange={setAprobarMeses}
        fecha={aprobarFecha}
        onFechaChange={setAprobarFecha}
        posologia={aprobarPosologia}
        onPosologiaChange={setAprobarPosologia}
        cantidad={aprobarCantidad}
        onCantidadChange={handleAprobarCantidadChange}
        dosificacion={aprobarDosificacion}
        onDosificacionChange={setAprobarDosificacion}
        emisorTratamiento={aprobarEmisorTratamiento}
        onEmisorTratamientoChange={handleAprobarEmisorChange}
        administrativosEmision={administrativosEmision}
        administrativosLoading={administrativosLoading}
        administrativoSeleccion={aprobarAdministrativoSeleccion}
        onAdministrativoSeleccionChange={setAprobarAdministrativoSeleccion}
        linkedAdministrativoNombre={linkedAdministrativoNombre}
        webhookAlta={aprobarWebhookAlta}
        onWebhookAltaChange={setAprobarWebhookAlta}
        webhookEmitir={aprobarWebhookEmitir}
        onWebhookEmitirChange={setAprobarWebhookEmitir}
        medicoSeleccion={aprobarMedicoSeleccion}
        onMedicoSeleccionChange={setAprobarMedicoSeleccion}
        medicoBuscar={aprobarMedicoBuscar}
        onMedicoBuscarChange={setAprobarMedicoBuscar}
        onConfirm={confirmarAprobacion}
        parseLocalDate={parseLocalDate}
        formatDniDisplay={formatDniDisplay}
        formatMedicoTitulo={formatMedicoTitulo}
        buildMedicationDisplay={buildMedicationDisplay}
      />

      {/* Modal Rechazar */}
      <div style={{ ...styles.modalBackdrop, opacity: modalRechazarOpen ? 1 : 0, pointerEvents: modalRechazarOpen ? 'all' : 'none' }}>
        <div style={{ ...styles.modal, width: isMobile ? '96vw' : 760 }}>
            {/* Cabecera informativa (copiada desde Aprobar) */}
            {selectedPedidoId && (() => {
              const pedido = data.pedidos.find(p => p.id === selectedPedidoId) || {};
              const badge = pedido.acepta_recordatorios
                ? (<span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '7px 16px',
                    borderRadius: 999,
                    background: 'linear-gradient(90deg,#10b981 60%,#34d399 100%)',
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: 13,
                    letterSpacing: '.04em',
                    marginRight: 18,
                    boxShadow: '0 2px 8px rgba(16,185,129,.13)',
                    textShadow: '0 1px 2px rgba(0,0,0,.10)'
                  }}>✔ Suscripto a recordatorios</span>)
                : (<span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '7px 16px',
                    borderRadius: 999,
                    background: 'linear-gradient(90deg,#f43f5e 60%,#f87171 100%)',
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: 13,
                    letterSpacing: '.04em',
                    marginRight: 18,
                    boxShadow: '0 2px 8px rgba(244,63,94,.13)',
                    textShadow: '0 1px 2px rgba(0,0,0,.10)'
                  }}>✖ Sin recordatorios</span>);
              return (
                <div style={{
                  background: '#f0fdfa',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 14,
                  border: '1px solid #99f6e4',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontWeight: 800, fontSize: 16, color: '#0f766e' }}>{pedido.paciente}</div>
                    <div style={{ fontSize: 13, color: '#334155' }}>
                      DNI {formatDniDisplay(pedido.dni)} · {pedido.obra_social}
                    </div>
                    <div style={{ fontSize: 13, color: '#0e7490', fontWeight: 700 }}>
                      Medicamento: {buildMedicationDisplay(pedido).title}
                    </div>
                    <div style={{ fontSize: 13, color: '#334155' }}>
                      Médico asignado: {formatMedicoTitulo(pedido.medico)}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>{badge}</div>
                </div>
              );
            })()}
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Rechazar pedido</div>
          <div style={{ color: 'var(--hs-text-xlight)', marginBottom: 12 }}>El paciente recibirá una notificación con el motivo</div>
          <input type="hidden" value={selectedPedidoId || ''} />
          <div>
            <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Motivo del rechazo</label>
            <textarea
              rows={4}
              value={rechazoMotivo}
              onChange={(e) => setRechazoMotivo(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--hs-border)', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hs-border)', background: 'transparent', cursor: 'pointer' }} onClick={() => setModalRechazarOpen(false)}>Cancelar</button>
            <button
              disabled={rechazoSubmitting || !rechazoMotivo.trim()}
              onClick={solicitarConfirmacionRechazo}
              style={{
                padding: '8px 12px', borderRadius: 8, border: 'none', background: (rechazoSubmitting || !rechazoMotivo.trim()) ? '#f7c7c9' : '#F2545B', color: '#fff', cursor: (rechazoSubmitting || !rechazoMotivo.trim()) ? 'not-allowed' : 'pointer'
              }}
            >✕ Confirmar rechazo</button>
          </div>
        </div>
      </div>

      <div onClick={(e) => { if (e.target === e.currentTarget) setModalEmitirOpen(false); }} style={{ ...styles.modalBackdrop, opacity: modalEmitirOpen ? 1 : 0, pointerEvents: modalEmitirOpen ? 'all' : 'none' }}>
        <div style={styles.modal}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Registrar emisión</div>
          <div style={{ color: 'var(--hs-text-xlight)', marginBottom: 12 }}>Ingresá el número de receta del sistema externo</div>
          <input type="hidden" value={selectedRecetaId || ''} />
          <div><label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>N° de receta (sistema externo)</label><input style={styles.formInput} value={emitirNro} onChange={(e) => setEmitirNro(e.target.value)} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hs-border)', background: 'transparent' }} onClick={() => setModalEmitirOpen(false)}>Cancelar</button>
            <button style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'var(--hs-yellow)', color: '#1a1a1a' }} onClick={confirmarEmision}>Registrar como emitida</button>
          </div>
        </div>
      </div>

      <div onClick={(e) => { if (e.target === e.currentTarget) setModalFarmaciaOpen(false); }} style={{ ...styles.modalBackdrop, opacity: modalFarmaciaOpen ? 1 : 0, pointerEvents: modalFarmaciaOpen ? 'all' : 'none' }}>
        <div style={styles.modal}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Gestionar pedido de farmacia</div>
          <div style={{ color: 'var(--hs-text-xlight)', marginBottom: 12 }}>Ingresá los datos para generar el pedido en farmacia</div>
          <input type="hidden" value={selectedFarmaciaId || ''} />
          <div><label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>N° de pedido en sistema de farmacia</label><input style={styles.formInput} value={farmaciaNro} onChange={(e) => setFarmaciaNro(e.target.value)} /></div>
          <div style={{ marginTop: 12 }}><label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Notas</label><textarea style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--hs-border)' }} value={farmaciaNotas} onChange={(e) => setFarmaciaNotas(e.target.value)} /></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hs-border)', background: 'transparent' }} onClick={() => setModalFarmaciaOpen(false)}>Cancelar</button>
            <button style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'var(--hs-navy)', color: '#fff' }} onClick={confirmarPedidoFarmacia}>Generar pedido en farmacia</button>
          </div>
        </div>
      </div>

      <div onClick={(e) => { if (e.target === e.currentTarget) setModalEstadoOpen(false); }} style={{ ...styles.modalBackdrop, opacity: modalEstadoOpen ? 1 : 0, pointerEvents: modalEstadoOpen ? 'all' : 'none' }}>
        <div style={styles.modal}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Actualizar estado</div>
          <div style={{ color: 'var(--hs-text-xlight)', marginBottom: 12 }}>Seleccioná el nuevo estado</div>
          <input type="hidden" value={selectedFarmaciaId || ''} />
          <div>
            <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Nuevo estado</label>
            <select id="estado-nuevo" style={styles.formInput}>
              <option value="pedido_generado_farmacia">Pedido generado en farmacia</option>
              <option value="en_preparacion">En preparación</option>
              <option value="en_camino">En camino</option>
              <option value="entregado">Entregado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
            <button style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--hs-border)', background: 'transparent' }} onClick={() => setModalEstadoOpen(false)}>Cancelar</button>
            <button style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: 'var(--hs-navy)', color: '#fff' }} onClick={() => confirmarEstadoFarmacia(document.getElementById('estado-nuevo').value)}>Actualizar</button>
          </div>
        </div>
      </div>

      {canAltaRapida ? (
        <AltaRapidaPedidoModal
          open={modalAltaRapidaOpen}
          onClose={() => setModalAltaRapidaOpen(false)}
          historialPacientes={historialPacientes}
          onPedidoCreated={handleAltaRapidaPedidoCreated}
          onError={(msg) => toast(msg, 'error')}
        />
      ) : null}

      <div onClick={(e) => { if (e.target === e.currentTarget) setModalHistorialGlobalOpen(false); }} style={{ position: 'fixed', inset: 0, background: modalHistorialGlobalOpen ? 'rgba(15,23,42,.26)' : 'rgba(15,23,42,0)', zIndex: 260, pointerEvents: modalHistorialGlobalOpen ? 'all' : 'none', transition: 'background .28s ease', display: 'flex', justifyContent: 'flex-end' }}>
        <aside style={{ width: isMobile ? '100vw' : '50vw', minWidth: isMobile ? 0 : 560, maxWidth: isMobile ? '100vw' : 860, height: '100dvh', background: 'linear-gradient(180deg,#ffffff,#f8fafc)', borderLeft: isMobile ? 'none' : '1px solid rgba(148,163,184,.26)', boxShadow: '-30px 0 70px rgba(15,23,42,.26), -8px 0 20px rgba(15,23,42,.12)', padding: isMobile ? 12 : 20, boxSizing: 'border-box', overflow: 'auto', transition: 'transform .42s cubic-bezier(.22,.9,.22,1)', transform: modalHistorialGlobalOpen ? 'translateX(0)' : 'translateX(105%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Historial global de tratamientos</div>
              <div style={{ color: 'var(--hs-text-xlight)', marginTop: 4 }}>Consultá historial aunque el pedido ya no esté en pendientes</div>
            </div>
            <button onClick={() => setModalHistorialGlobalOpen(false)} style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(148,163,184,.35)', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>Cerrar</button>
          </div>

          <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr 1.3fr auto', gap: 10, alignItems: 'end' }}>
            <div style={{ fontSize: 12, color: 'var(--hs-text-xlight)', fontWeight: 700, marginBottom: 10 }}>Paciente (DNI)</div>
            <input
              value={historialGlobalPacienteSearch}
              onChange={(e) => setHistorialGlobalPacienteSearch(e.target.value)}
              placeholder="Buscar paciente en el selector..."
              style={{ ...styles.formInput }}
            />
            <select
              value={historialGlobalDni}
              onChange={(e) => setHistorialGlobalDni(e.target.value)}
              style={{ ...styles.formInput, cursor: 'pointer' }}
            >
              <option value="">{filteredHistorialPacientes.length === 0 ? 'Sin coincidencias' : 'Seleccioná un paciente'}</option>
              {filteredHistorialPacientes.map((p) => (
                <option key={p.dni} value={p.dni}>{p.paciente} · DNI {formatDniDisplay(p.dni)}{p.obra_social ? ` · ${p.obra_social}` : ''}</option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: '#475569', background: 'rgba(148,163,184,.12)', borderRadius: 999, padding: '6px 10px', fontWeight: 700, marginTop: 22 }}>
              {filteredHistorialGlobalItems.length} registros
            </div>
          </div>
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr .8fr', gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Buscar en historial global</label>
              <input
                value={historialGlobalSearch}
                onChange={(e) => setHistorialGlobalSearch(e.target.value)}
                placeholder="Medicamento, estado, médico, notas..."
                disabled={!historialGlobalDni}
                style={{ ...styles.formInput, opacity: historialGlobalDni ? 1 : 0.6, cursor: historialGlobalDni ? 'text' : 'not-allowed' }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Estado</label>
              <select value={historialGlobalEstado} onChange={(e) => setHistorialGlobalEstado(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, cursor: historialGlobalDni ? 'pointer' : 'not-allowed', opacity: historialGlobalDni ? 1 : 0.6 }}>
                <option value="todos">Todos</option>
                <option value="pendiente_aprobacion_medica">Pendiente médico</option>
                <option value="aprobado">Aprobado</option>
                <option value="rechazado">Rechazado</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr auto auto', gap: 10, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Desde</label>
              <input type="date" value={historialGlobalDesde} onChange={(e) => setHistorialGlobalDesde(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, opacity: historialGlobalDni ? 1 : 0.6, cursor: historialGlobalDni ? 'text' : 'not-allowed' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Hasta</label>
              <input type="date" value={historialGlobalHasta} onChange={(e) => setHistorialGlobalHasta(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, opacity: historialGlobalDni ? 1 : 0.6, cursor: historialGlobalDni ? 'text' : 'not-allowed' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Orden</label>
              <select value={historialGlobalOrder} onChange={(e) => setHistorialGlobalOrder(e.target.value)} disabled={!historialGlobalDni} style={{ ...styles.formInput, cursor: historialGlobalDni ? 'pointer' : 'not-allowed', opacity: historialGlobalDni ? 1 : 0.6 }}>
                <option value="fecha_desc">Mas reciente</option>
                <option value="fecha_asc">Mas antiguo</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--hs-text-xlight)', marginBottom: 6, display: 'block' }}>Mostrar</label>
              <select value={historialGlobalItemsPerPage} onChange={(e) => setHistorialGlobalItemsPerPage(Number(e.target.value))} disabled={!historialGlobalDni} style={{ ...styles.formInput, cursor: historialGlobalDni ? 'pointer' : 'not-allowed', minWidth: 90, opacity: historialGlobalDni ? 1 : 0.6 }}>
                <option value={6}>6</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredHistorialGlobalItems.length === 0 ? (
              <div className="crm-icard-b" style={{ textAlign: 'center', color: 'var(--hs-text-xlight)', border: '1px solid rgba(148,163,184,.2)', borderRadius: 14, background: 'linear-gradient(180deg,#ffffff,#f8fafc)', padding: '28px 18px', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.75)' }}>
                <div style={{ width: 56, height: 56, margin: '0 auto', borderRadius: '50%', background: 'linear-gradient(145deg,#eef2ff,#dbeafe)', border: '1px solid rgba(37,99,235,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#1d4ed8', boxShadow: '0 8px 20px rgba(37,99,235,.16)' }}>
                  🗂️
                </div>
                <div style={{ marginTop: 12, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Sin historial para mostrar</div>
                <div style={{ marginTop: 6, fontSize: 13, color: '#64748b', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.45 }}>
                  Seleccioná otro paciente o ajustá el buscador para encontrar tratamientos aprobados o rechazados.
                </div>
              </div>
            ) : (
              pageHistorialGlobalItems.map((h) => {
                const isPendienteDerivacion = h.estado === 'pendiente_derivacion';
                return (
                <div key={`hist-global-${h.id}`} style={{ padding: '10px 12px', borderRadius: 12, border: isPendienteDerivacion ? '1px dashed rgba(148,163,184,.35)' : '1px solid rgba(148,163,184,.2)', background: isPendienteDerivacion ? 'linear-gradient(180deg,rgba(248,250,252,.72),rgba(241,245,249,.56))' : 'linear-gradient(180deg,#ffffff,#f8fafc)', opacity: isPendienteDerivacion ? 0.72 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 14 }}>{h.medicamento || 'Medicamento sin dato'}</div>
                      {h.nombre_comercial ? (
                        <div style={{ marginTop: 4, fontSize: 12, color: '#33475B' }}>
                          {(() => {
                            const clean = (v) => {
                              const s = String(v ?? '').trim();
                              if (!s) return '';
                              const low = s.toLowerCase();
                              return (low === '0' || low === 'null' || low === 'undefined') ? '' : s;
                            };
                            const marca = clean(h.nombre_comercial);
                            const lab = clean(h.laboratorio);
                            return [marca, lab].filter(Boolean).join(' · ');
                          })()}
                        </div>
                      ) : null}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {h.estado === 'aprobado' ? (
                          <button
                            onClick={() => abrirVerRecetaDesdeHistorial(h)}
                            style={{
                              padding: '4px 9px',
                              borderRadius: 999,
                              border: '1px solid #2365ab',
                              background: '#fff',
                              color: '#2365ab',
                              fontWeight: 800,
                              fontSize: 10,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 5
                            }}
                          >
                            <span style={{ fontSize: 10 }}>↗</span>
                            Ver receta
                          </button>
                        ) : null}
                        {estadoPillElem(h, { nowMs: colaTick })}
                      </div>
                      {h.fecha_solicitud ? <div style={{ fontSize: 11, color: 'var(--hs-text-xlight)', marginTop: 6 }}>{formatSolicitud(h.fecha_solicitud)}</div> : null}
                    </div>
                  </div>
                  {h.posologia ? <div style={{ marginTop: 8, fontSize: 13, color: '#516F90' }}><strong>Posología:</strong> {h.posologia}</div> : null}
                  <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: '#33475B', fontWeight: 700 }}>{formatDoctorDisplayName(h.medico)}{h.periodo ? ` · ${h.periodo}` : ''}</div>
                    <div style={{ fontSize: 12, color: 'var(--hs-text-xlight)' }}>{h.recetas_generadas || 0}/{h.cantidad_recetas || 0} recetas</div>
                  </div>
                  {h.estado === 'rechazado' ? (
                    (h.motivo_rechazo || h.notas) ? <div style={{ marginTop: 8, fontSize: 13, color: '#516F90' }}><strong>Motivo de rechazo:</strong> {h.motivo_rechazo || h.notas}</div> : null
                  ) : (
                    h.notas ? <div style={{ marginTop: 8, fontSize: 13, color: '#516F90' }}><strong>Notas:</strong> {h.notas}</div> : null
                  )}
                </div>
                );
              })
            )}
          </div>
          {filteredHistorialGlobalItems.length > 0 ? (
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Mostrando {Math.min((historialGlobalPage - 1) * historialGlobalItemsPerPage + 1, filteredHistorialGlobalItems.length)}-{Math.min(historialGlobalPage * historialGlobalItemsPerPage, filteredHistorialGlobalItems.length)} de {filteredHistorialGlobalItems.length}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button disabled={historialGlobalPage <= 1} onClick={() => setHistorialGlobalPage((p) => Math.max(1, p - 1))} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.3)', background: historialGlobalPage <= 1 ? '#f1f3f6' : '#fff', cursor: historialGlobalPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 700 }}>◀</button>
                <div style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>Página {historialGlobalPage} / {totalHistorialGlobalPages}</div>
                <button disabled={historialGlobalPage >= totalHistorialGlobalPages} onClick={() => setHistorialGlobalPage((p) => Math.min(totalHistorialGlobalPages, p + 1))} style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid rgba(148,163,184,.3)', background: historialGlobalPage >= totalHistorialGlobalPages ? '#f1f3f6' : '#fff', cursor: historialGlobalPage >= totalHistorialGlobalPages ? 'not-allowed' : 'pointer', fontWeight: 700 }}>▶</button>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <div onClick={(e) => { if (e.target === e.currentTarget) cerrarVerReceta(); }} style={{ position: 'fixed', inset: 0, background: modalVerRecetaOpen ? 'rgba(15,23,42,.28)' : 'rgba(15,23,42,0)', zIndex: 280, pointerEvents: modalVerRecetaOpen ? 'all' : 'none', transition: 'background .28s ease', display: 'flex', justifyContent: 'flex-start' }}>
        <aside style={{ width: isMobile ? '100vw' : '52vw', minWidth: isMobile ? 0 : 560, maxWidth: isMobile ? '100vw' : 920, height: '100dvh', background: 'linear-gradient(180deg,#ffffff,#f8fafc)', borderRight: isMobile ? 'none' : '1px solid rgba(148,163,184,.26)', boxShadow: '30px 0 70px rgba(15,23,42,.22), 8px 0 20px rgba(15,23,42,.10)', padding: isMobile ? 12 : 20, boxSizing: 'border-box', overflow: 'auto', transition: 'transform .42s cubic-bezier(.22,.9,.22,1)', transform: modalVerRecetaOpen ? 'translateX(0)' : 'translateX(-105%)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>Tratamiento aprobado</div>
              <div style={{ color: 'var(--hs-text-xlight)', marginTop: 4 }}>Vista profesional para control e impresión</div>
            </div>
            <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              <button onClick={imprimirVistaReceta} style={{ padding: '8px 12px', borderRadius: 999, border: '1px solid #2365ab', background: 'rgba(35,101,171,.08)', color: '#2365ab', cursor: 'pointer', fontWeight: 800 }}>Imprimir</button>
              <button onClick={cerrarVerReceta} style={{ padding: '8px 12px', borderRadius: 999, border: '1px solid rgba(148,163,184,.35)', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>Volver</button>
            </div>
          </div>

          <div style={{ marginTop: 12, border: '1px solid rgba(37,99,235,.2)', borderRadius: 20, overflow: 'hidden', background: 'linear-gradient(180deg,#ffffff,#fbfdff)', boxShadow: '0 22px 50px rgba(15,23,42,.09)' }}>
            <div style={{ padding: '18px 20px', borderBottom: '1px solid rgba(148,163,184,.18)', background: 'linear-gradient(180deg,#f0f7ff,#ffffff)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 19, color: '#0f172a', letterSpacing: '.01em' }}>{verRecetaPedido?.paciente || 'Paciente sin dato'}</div>
                <div style={{ marginTop: 4, fontSize: 12, color: '#334155' }}>DNI {formatDniDisplay(verRecetaPedido?.dni || verRecetaPedido?.dni_normalizado)} · OS {verRecetaPedido?.obra_social || '—'}</div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#2365ab', border: '1px solid rgba(35,101,171,.28)', borderRadius: 999, padding: '5px 11px', background: 'rgba(35,101,171,.06)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Tratamiento aprobado
              </div>
            </div>

            <div style={{ padding: '16px 18px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
              <div style={{ border: '1px solid rgba(148,163,184,.18)', borderRadius: 14, background: '#fff', padding: '11px 13px' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', fontWeight: 800 }}>Contexto clínico</div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ minWidth: 68 }}>Médico:</span><strong style={{ color: '#0f172a' }}>{verRecetaMedicoLabel}</strong></div>
                <div style={{ marginTop: 5, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ minWidth: 68 }}>Posología:</span><strong style={{ color: '#0f172a' }}>{verRecetaPedido?.posologia || '—'}</strong></div>
                <div style={{ marginTop: 6, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ minWidth: 68 }}>Estado:</span>{estadoPillElem(verRecetaPedido || { estado: 'pendiente_aprobacion_medica' }, { nowMs: colaTick })}</div>
              </div>
              <div style={{ border: '1px solid rgba(148,163,184,.18)', borderRadius: 14, background: '#fff', padding: '11px 13px' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', fontWeight: 800 }}>Control de recetas</div>
                <div style={{ marginTop: 8, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ minWidth: 68 }}>Cantidad:</span><strong style={{ color: '#0f172a' }}>{verRecetaPedido?.cantidad_recetas || 0}</strong></div>
                <div style={{ marginTop: 5, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ minWidth: 68 }}>Emitidas:</span><strong style={{ color: '#0f172a' }}>{verRecetaEmitidas}</strong></div>
                <div style={{ marginTop: 5, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ minWidth: 68 }}>Solicitud:</span><strong style={{ color: '#0f172a' }}>{verRecetaPedido?.fecha_solicitud ? formatSolicitud(verRecetaPedido.fecha_solicitud) : '—'}</strong></div>
                <div style={{ marginTop: 5, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ minWidth: 68 }}>Inicio:</span><strong style={{ color: '#0f172a' }}>{verRecetaPedido?.fecha_inicio ? formatSolicitud(normalizeToIsoDate(verRecetaPedido.fecha_inicio) || verRecetaPedido.fecha_inicio) : '—'}</strong></div>
                <div style={{ marginTop: 5, fontSize: 12, color: '#334155', display: 'flex', alignItems: 'baseline', gap: 8 }}><span style={{ minWidth: 68 }}>Fin:</span><strong style={{ color: '#0f172a' }}>{verRecetaFechaFinLabel}</strong></div>
              </div>
            </div>

            <div style={{ padding: '0 18px 18px 18px' }}>
              <div style={{ border: '1px solid rgba(148,163,184,.18)', borderRadius: 14, background: '#fff', padding: '12px 14px' }}>
                <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', fontWeight: 800 }}>Medicamento</div>
                <div style={{ marginTop: 8, fontSize: 15, color: '#0f172a', fontWeight: 900 }}>{toTitleCaseText(verRecetaPedido?.medicamento_generico ? `${verRecetaPedido?.medicamento_generico} ${verRecetaPedido?.medicamento_concentracion || ''}` : verRecetaPedido?.medicamento) || '—'}</div>
                <div style={{ marginTop: 5, fontSize: 12, color: '#334155' }}>
                  {(() => {
                    const clean = (v) => {
                      const s = String(v ?? '').trim();
                      if (!s) return '';
                      const low = s.toLowerCase();
                      return (low === '0' || low === 'null' || low === 'undefined') ? '' : s;
                    };
                    const marca = clean(verRecetaPedido?.nombre_comercial);
                    const lab = clean(verRecetaPedido?.medicamento_laboratorio);
                    return [marca, lab].filter(Boolean).join(' · ') || 'Sin marca comercial';
                  })()}
                </div>
                {verRecetaPedido?.periodo_meses ? (
                  <div style={{ marginTop: 5, fontSize: 12, color: '#334155' }}>Periodo: <strong style={{ color: '#0f172a' }}>{`${verRecetaPedido.periodo_meses} meses`}</strong></div>
                ) : null}
              </div>
              {verRecetaEmitidasDetalle.length ? (
                <div style={{ marginTop: 10, border: '1px solid rgba(148,163,184,.18)', borderRadius: 14, background: '#fff', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b', fontWeight: 800 }}>Recetas emitidas</div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#2365ab', border: '1px solid rgba(35,101,171,.25)', borderRadius: 999, padding: '3px 9px', background: 'rgba(35,101,171,.06)' }}>
                      {verRecetaEmitidasDetalle.length} registradas
                    </div>
                  </div>
                  <div style={{ marginTop: 9, display: 'grid', gap: 8 }}>
                    {verRecetaEmitidasDetalle.map((r) => (
                      <div key={`emitida-det-${r.id || r.numero}`} style={{ border: '1px solid rgba(148,163,184,.16)', borderRadius: 12, background: 'linear-gradient(180deg,#ffffff,#f8fbff)', padding: '9px 11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>Receta {r.numero || '—'}{(r.nro_externo || r.nroExterno) ? ` · N° ${r.nro_externo || r.nroExterno}` : ''}</div>
                          <div style={{ marginTop: 3, fontSize: 11, color: '#64748b' }}>
                            {r.fecha_prevista ? `Prevista: ${r.fecha_prevista} · ` : ''}
                            Emitida: {r.fecha_emitida || '—'}
                          </div>
                        </div>
                        <div>{recetaPillElem(r)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {verRecetaPedido?.notas ? (
                <div style={{ marginTop: 10, border: '1px solid rgba(148,163,184,.18)', borderRadius: 14, background: '#fff', padding: '12px 14px', fontSize: 12, color: '#334155' }}>
                  <strong>Notas:</strong> {verRecetaPedido.notas}
                </div>
              ) : null}
            </div>
          </div>
        </aside>
      </div>

      <div className="toast-container" style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1200 }}>
        {toasts.map(t => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff', color: '#253342', boxShadow: '0 18px 50px rgba(8,18,40,0.18), 0 6px 18px rgba(8,18,40,0.08)', borderRadius: 12, padding: '10px 12px', marginTop: 8, minWidth: 260, maxWidth: 420, height: 'auto', transform: 'translateZ(0)' }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.type === 'success' ? '#E6FBF7' : t.type === 'error' ? '#FFF6F6' : '#F4F7FF', color: t.type === 'success' ? '#00BDA5' : t.type === 'error' ? '#F2545B' : '#2365AB', fontWeight: 800, fontSize: 14 }}>
              <span>{t.type === 'success' ? '✓' : t.type === 'error' ? '✕' : 'ℹ'}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ fontSize: 13, fontWeight: 800, lineHeight: '1.1', wordBreak: 'break-word', maxWidth: 340 }}>{t.paciente || t.msg}</div>
              {t.sus || t.subtitle ? (
                <div style={{ fontSize: 12, color: '#6B7D93', marginTop: 4, wordBreak: 'break-word', maxWidth: 340 }}>{t.sus || t.subtitle}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
